import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { Mixer } from '../components/Mixer';
import { RightsModal } from '../components/RightsModal';
import { DownloadAttestationModal } from '../components/DownloadAttestationModal';
import { Waveform, computePeaks, seededBars } from '../components/Waveform';
import { Icons, stemMark, STEM_UI, STEM_COLOR } from '../components/studioIcons';
import { sections as sectionsApi, usage as usageApi, loops as loopsApi, Section, UsageSummary, type ApiError } from '../lib/api';
import {
  loadYouTubeAPI,
  parseYtVideoId,
  embedErrorMessage,
  tabCaptureSupported,
  startCapture,
  captureErrorMessage,
  CaptureError,
  YT_STATE,
  type CapturePhase,
  type CaptureHandle,
  type YtPlayer,
} from '../lib/ytCapture';
import { parseSoundCloudUrl, createScPlayer, scEmbedErrorMessage, type ScPlayer } from '../lib/scCapture';
import { separateClientSide, decodeFileToStereo, preloadEngine, prefetchStandardEngine, type SepEngine } from '../lib/clientSep';
import { soloStem, soloGlyph, type SoloRegion } from '../lib/soloDetect';
import { fetchYtSuggestions, type Suggestion } from '../lib/ytSuggest';
import { detectKeyBpm, type KeyBpmResult } from '../lib/keyBpm';
import {
  StemEngine,
  DEFAULT_MIX,
  MIX_PRESETS,
  MixState,
  STEMS,
  StemName,
  audibleVolume,
} from '../lib/studioEngine';
import { renderAndEncode, renderMix, audioBufferToWav, saveBlob, safeBaseName, m4aSupported, FORMAT_EXT, type ExportFormat } from '../lib/audioExport';
import { STEM_META, fmtTime } from '../lib/format';
import { useAuth } from '../lib/auth';
import { hasAcknowledgedLocally } from '../lib/rights';
import { EditableName } from '../components/EditableName';
import {
  cacheSeparation,
  linkSection,
  unlinkSection,
  loadSectionIndex,
  getSeparation,
  getSourceBlob,
  stemUrlsFrom,
  newSeparationKey,
  type CachedSeparation,
} from '../lib/stemCache';
import { wavToOpus } from '../lib/opus';
import { takePendingUpload } from '../lib/pendingUpload';
import { claimHandoff } from '../lib/handoffClaim';
import './studio.css';

type Mode = 'input' | 'working' | 'playing' | 'error';
type InputTab = 'upload' | 'url' | 'soundcloud';

// Mirrors the backend's YOUTUBE_RE so obvious non-links fail instantly,
// without a round-trip. The backend stays authoritative.
const YT_LINK_RE = /^(https?:\/\/)?(www\.|m\.)?(youtube\.com\/(watch\?\S*v=|shorts\/|embed\/)|youtu\.be\/)[\w-]{6,}/i;

// Intent = the user's sentence: a PART (the six stem chips are nouns) plus a
// VERB carried by the split CTA — isolate it, or play without it. Every run
// produces all six stems either way; the verb only sets the starting mix.
type IntentVerb = 'isolate' | 'without';

const INTENT_LS_KEY = 'rl_intent_v1';

// Last-used intent, written ONLY after a completed run (so a deep-link visit
// that bounces doesn't pollute a musician's saved default).
function loadStoredIntent(): { verb: IntentVerb; part: StemName } | null {
  try {
    const raw = localStorage.getItem(INTENT_LS_KEY);
    if (!raw) return null;
    const j = JSON.parse(raw) as { verb?: string; part?: string };
    if ((j.verb === 'isolate' || j.verb === 'without') && (STEMS as string[]).includes(j.part || '')) {
      return { verb: j.verb, part: j.part as StemName };
    }
  } catch {
    /* ignore */
  }
  return null;
}
function storeIntent(verb: IntentVerb, part: StemName) {
  try {
    localStorage.setItem(INTENT_LS_KEY, JSON.stringify({ verb, part }));
  } catch {
    /* ignore */
  }
}

// Goal-named deep-link aliases (?goal=karaoke etc.) for the SEO landing pages
// — the searcher's own word arrives pre-armed. karaoke additionally lands the
// mixer on the Karaoke preset (15% vocal guide) after the run.
const GOAL_ALIASES: Record<string, { verb: IntentVerb; part: StemName; karaoke?: boolean }> = {
  karaoke: { verb: 'without', part: 'vocals', karaoke: true },
  'vocal-remover': { verb: 'without', part: 'vocals' },
  instrumental: { verb: 'without', part: 'vocals' },
  'backing-track': { verb: 'without', part: 'vocals' },
  acapella: { verb: 'isolate', part: 'vocals' },
  drumless: { verb: 'without', part: 'drums' },
};

// Views/likes/duration for the preview card, from our own /api/yt-meta proxy
// (the YouTube Data API key lives server-side). Session-cached per video id so
// re-pasting the same link doesn't re-hit the backend. Returns null on any
// failure (missing endpoint on an old backend, quota, offline) — the caller
// then just keeps the oEmbed-only card.
async function ytMeta(videoId: string): Promise<{ views: number | null; likes: number | null; durationSec: number } | null> {
  const cacheKey = 'rl_ytmeta_' + videoId;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);
  } catch {
    /* ignore */
  }
  try {
    const res = await fetch('/api/yt-meta?v=' + encodeURIComponent(videoId), { credentials: 'include' });
    if (!res.ok) return null;
    const j = (await res.json()) as { ok?: boolean; views?: number | null; likes?: number | null; durationSec?: number };
    if (!j.ok) return null;
    const out = { views: j.views ?? null, likes: j.likes ?? null, durationSec: j.durationSec ?? 0 };
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(out));
    } catch {
      /* ignore */
    }
    return out;
  } catch {
    return null;
  }
}

// Preview state for a pasted YouTube link. 'bad' = YouTube rejected the video
// (private/removed); 'unknown' = oEmbed unreachable, preview unavailable but
// the audio fetch may still work.
type LinkMeta =
  | { status: 'validating' }
  | { status: 'ok'; title: string; author: string; thumb: string; views?: number | null; likes?: number | null; durationSec?: number }
  | { status: 'bad' }
  | { status: 'unknown' };

// Compact count, YouTube-style: 651196 → "651K", 8473 → "8.5K", 2_100_000 → "2.1M".
function compactCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) {
    const k = n / 1000;
    return (k >= 100 ? Math.round(k) : Math.round(k * 10) / 10) + 'K';
  }
  const m = n / 1_000_000;
  return (m >= 100 ? Math.round(m) : Math.round(m * 10) / 10) + 'M';
}

interface Working {
  step: string;
  message: string;
}

// Decoded upload kept in a ref — the full-track waveform is drawn from it and
// the isolate step slices its window (no second decode).
interface Decoded {
  left: Float32Array;
  right: Float32Array;
  sampleRate: number;
  durationSec: number;
}

// Lightweight metadata for the loaded-track view (peaks for the waveform).
interface TrackInfo {
  name: string;
  size: number;
  durationSec: number;
  peaks: number[];
  objectUrl: string;
}

interface Loaded {
  stemUrls: Record<StemName, string>;
  emptyStems: Set<StemName>;
  start: number;
  end: number;
  primaryStem: StemName;
  // What the run promised: hear only primaryStem ('isolate') or the full mix
  // minus it ('without'). Drives the starting fader mix + result labels.
  intent: IntentVerb;
  // Human title of the source (YouTube video title / clean upload name) so
  // saved sections and the "continue where you left off" card name the actual
  // song instead of a generic "YouTube clip".
  sourceTitle?: string;
  sourceKind: 'url' | 'upload';
  sourceRef: string;
  stemsCacheId: string | null;
  clientStemsKey: string | null; // local IndexedDB key; lets saved sections re-open after reload
  peaks: number[]; // window peaks for the result waveform
  // Auto-detected section types (Guitar solo, Drum solo, …) with times RELATIVE
  // to this clip (0..end-start). Empty when nothing distinctive was found.
  solos: SoloRegion[];
}

export function Studio() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('input');
  // SoundCloud is a full tab for everyone (promoted 2026-07-07 after the
  // owner verified playback). /soundcloud remains as a deep link that lands
  // straight on the tab.
  const [tab, setTab] = useState<InputTab>(location.pathname.startsWith('/soundcloud') ? 'soundcloud' : 'upload');
  const [url, setUrl] = useState('');
  // Pasted SoundCloud link + the validated track panel (mirrors url/capturePanel
  // for YouTube). scPanel non-null → the SoundCloud embed + capture step is up.
  const [scUrl, setScUrl] = useState('');
  const [scPanel, setScPanel] = useState<null | { trackUrl: string; title: string }>(null);
  // Non-null while the "From link" tab shows the embedded player + capture
  // step for a validated link. The audio comes from capturing THIS tab while
  // the official embed plays — no backend touches YouTube.
  const [capturePanel, setCapturePanel] = useState<null | {
    videoId: string;
    url: string;
    title: string;
    author: string;
  }>(null);
  // Instant preview of the pasted link (title/channel/thumbnail via YouTube's
  // oEmbed endpoint — CORS-open, no key, no backend). status 'bad' means
  // YouTube answered but rejected the video (private/removed): surface that
  // immediately instead of 20s into the audio fetch. A network-level failure
  // keeps status 'ok'-less but does NOT block loading — oEmbed being
  // unreachable doesn't mean the fetch will fail.
  const [linkMeta, setLinkMeta] = useState<LinkMeta | null>(null);
  useEffect(() => {
    const raw = url.trim();
    if (!YT_LINK_RE.test(raw)) {
      setLinkMeta(null);
      return;
    }
    let stale = false;
    setLinkMeta({ status: 'validating' });
    // Small debounce so a hand-typed URL doesn't fire a request per keystroke.
    const t = setTimeout(async () => {
      try {
        const res = await fetch('https://www.youtube.com/oembed?format=json&url=' + encodeURIComponent(raw));
        if (stale) return;
        if (!res.ok) {
          setLinkMeta({ status: 'bad' });
          return;
        }
        const j = (await res.json()) as { title?: string; author_name?: string; thumbnail_url?: string };
        if (stale) return;
        setLinkMeta({
          status: 'ok',
          title: j.title || 'YouTube video',
          author: j.author_name || '',
          thumb: j.thumbnail_url || '',
        });
        // Enrich with views/likes/duration from our own server-side proxy
        // (YouTube Data API key stays on the backend). Purely additive — if the
        // endpoint is missing or fails, the oEmbed card above still stands.
        const vid = parseYtVideoId(raw);
        if (vid) {
          const enrich = await ytMeta(vid);
          if (!stale && enrich) {
            setLinkMeta((m) =>
              m && m.status === 'ok'
                ? { ...m, views: enrich.views, likes: enrich.likes, durationSec: enrich.durationSec }
                : m,
            );
          }
        }
      } catch {
        if (!stale) setLinkMeta({ status: 'unknown' });
      }
    }, 350);
    return () => {
      stale = true;
      clearTimeout(t);
    };
  }, [url]);

  // Idle prefetch of the Standard engine into durable storage — most users
  // then never watch the engine download at separation time.
  useEffect(() => {
    prefetchStandardEngine();
  }, []);
  // Set when the loaded track came from a YouTube link: threads the video id
  // + canonical URL through quota telemetry, export gating and saved
  // sections. Null for uploads; cleared whenever a new file replaces it.
  const linkSourceRef = useRef<{ videoId: string; url: string; title?: string } | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [trackInfo, setTrackInfo] = useState<TrackInfo | null>(null);
  const [decoding, setDecoding] = useState(false);
  // Client-side key + BPM estimate for the loaded track (null while analysing).
  const [keyBpm, setKeyBpm] = useState<KeyBpmResult | null>(null);
  const [analyzingKb, setAnalyzingKb] = useState(false);
  const [region, setRegion] = useState<{ start: number; end: number }>({ start: 0, end: 30 });
  // Intent boot: deep link (?goal=… / ?intent=…&part=…, session-scoped) wins
  // over the stored last-used intent, which wins over the vocals default.
  const intentBootRef = useRef<{ verb: IntentVerb; part: StemName; karaoke: boolean; source: 'deeplink' | 'memory' | 'default' } | null>(null);
  if (!intentBootRef.current) {
    const q = new URLSearchParams(location.search);
    const goal = GOAL_ALIASES[(q.get('goal') || '').toLowerCase()];
    const qVerb = q.get('intent');
    const rawPart = (q.get('part') || '').toLowerCase();
    const qPart = rawPart === 'lead' ? 'other' : rawPart;
    if (goal) {
      intentBootRef.current = { verb: goal.verb, part: goal.part, karaoke: !!goal.karaoke, source: 'deeplink' };
    } else if ((qVerb === 'isolate' || qVerb === 'without') && (STEMS as string[]).includes(qPart)) {
      intentBootRef.current = { verb: qVerb, part: qPart as StemName, karaoke: false, source: 'deeplink' };
    } else {
      const mem = loadStoredIntent();
      intentBootRef.current = mem
        ? { ...mem, karaoke: false, source: 'memory' }
        : { verb: 'isolate', part: 'vocals', karaoke: false, source: 'default' };
    }
  }
  const [chosenStem, setChosenStem] = useState<StemName>(intentBootRef.current.part);
  const [chosenVerb, setChosenVerb] = useState<IntentVerb>(intentBootRef.current.verb);
  // True until the very first completed run on this device — drives the
  // "you get all six parts either way" reassurance caption.
  const firstRunRef = useRef(!loadStoredIntent());
  // When on, run client-side separation with shift ensembling (shifts=2 —
  // demucs's reference quality trick; cleaner stems, ~2× slower in-browser).
  // Which model: demucs (htdemucs_6s, 142 MB) or BS-RoFormer-SW (336 MB).
  const [sepEngine, setSepEngine] = useState<SepEngine>('demucs');
  const [working, setWorking] = useState<Working | null>(null);
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [quotaModal, setQuotaModal] = useState<null | 'anon' | 'free'>(null);
  const [loopCapModal, setLoopCapModal] = useState(false);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [mix, setMix] = useState<MixState>(DEFAULT_MIX);
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [time, setTime] = useState(0);
  // Post-separation trim: a sub-window [start,end] (seconds, relative to the
  // separated clip) the user can shrink from both ends to keep a shorter
  // version. Defaults to the full clip on load. `trimming` guards the commit.
  const [trim, setTrim] = useState<{ start: number; end: number }>({ start: 0, end: 0 });
  const [trimming, setTrimming] = useState(false);
  const [savedSections, setSavedSections] = useState<Section[]>([]);
  const [usageInfo, setUsageInfo] = useState<UsageSummary | null>(null);
  // sectionId → local stem-cache key, for sections whose stems are cached on
  // this device (so they re-open fully after a reload).
  const [sectionKeys, setSectionKeys] = useState<Map<string, string>>(() => new Map());
  // The saved section the current result represents (set on reopen / after the
  // first save) so a re-save can offer to update it instead of always making a
  // new one.
  const [currentSectionId, setCurrentSectionId] = useState<string | null>(null);
  const [saveChoiceOpen, setSaveChoiceOpen] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [keysReady, setKeysReady] = useState(false);
  const reopenedRef = useRef(false);
  // Set when we arrive from the splitter funnel (?handoff=…): once the claimed
  // file finishes decoding, kick off separation automatically so the visitor
  // lands on a ready result, not an empty picker. Armed ONLY after the claim
  // actually returns a file (never up front), so a stray pending-upload or a
  // failed claim can't auto-separate the wrong thing.
  const autoSepRef = useRef(false);
  // Guards the handoff claim to exactly once (StrictMode double-mounts effects
  // in dev; the bridge deletes the record on first read, so a second claim
  // would resolve null).
  const claimStartedRef = useRef(false);
  // When a separation is kicked off automatically (handoff auto-claim, no user
  // gesture), leave the result CUED/paused instead of auto-playing: the browser
  // keeps the AudioContext suspended without a gesture, so auto-play is silent.
  // The user's first Play click then resumes audio. (loadStems consumes this.)
  const skipAutoPlayRef = useRef(false);
  const [rightsOpen, setRightsOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProOpen, setExportProOpen] = useState(false); // free-user "export is Pro" upsell
  const [bestProOpen, setBestProOpen] = useState(false); // free-user "Best quality is Pro" upsell
  const pendingExport = useRef<{ target: 'mix' | StemName; format: ExportFormat } | null>(null); // what to run after the download-rights ack
  // Optimistic sync init (WebCodecs present ⇒ assume AAC) so M4A is the DEFAULT from
  // the first render on Chrome with no format flicker; the async probe refines it
  // (e.g. the rare AudioEncoder-but-no-AAC case → drops M4A).
  const [m4aOk, setM4aOk] = useState(() => typeof AudioEncoder !== 'undefined' && typeof AudioData !== 'undefined');
  useEffect(() => { m4aSupported().then(setM4aOk).catch(() => setM4aOk(false)); }, []);
  // Export formats offered (MP3 · M4A · WAV, per Moises); M4A only where supported.
  const exportFormats: ExportFormat[] = m4aOk ? ['mp3', 'm4a', 'wav'] : ['mp3', 'wav'];

  const pendingAction = useRef<(() => void) | null>(null);
  const decodedRef = useRef<Decoded | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const engineRef = useRef<StemEngine | null>(null);
  if (!engineRef.current) engineRef.current = new StemEngine();
  const engine = engineRef.current;

  useEffect(() => {
    engine.setCallbacks({
      onTimeUpdate: (t) => setTime(t),
      onEnded: () => setMix((m) => ({ ...m, playing: false })),
    });
    return () => engine.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    engine.applyMix(mix);
  }, [mix, engine]);

  // Free decoded buffers + object URL on unmount.
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    sectionsApi.list().then(setSavedSections).catch(() => {});
    usageApi.me().then(setUsageInfo).catch(() => {});
    loadSectionIndex()
      .then((m) => {
        setSectionKeys(m);
        setKeysReady(true);
      })
      .catch(() => setKeysReady(true));
  }, [user]);

  // Dashboard deep-links: a dropped file (handed over via module state) and a
  // pre-chosen stem (router state). Run once on mount.
  useEffect(() => {
    const f = takePendingUpload();
    if (f) pickFile(f);
    const st = (location.state as { stem?: string } | null)?.stem;
    if (st && (STEMS as string[]).includes(st)) setChosenStem(st as StemName);

    // Funnel handoff: the splitter page on riffloop.app stashed a file in the
    // browser and sent us here as ?handoff=<id>. Pull it across the same-site
    // bridge (no server upload) and auto-separate it. Strip the param first so
    // a refresh can't re-claim a now-deleted record.
    const handoffId = new URLSearchParams(location.search).get('handoff');
    if (handoffId && !claimStartedRef.current) {
      claimStartedRef.current = true;
      navigate(location.pathname, { replace: true });
      claimHandoff(handoffId)
        .then((file) => {
          if (file) {
            autoSepRef.current = true; // arm only now that we truly have the file
            pickFile(file);
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once a funnel-claimed file has decoded (trackInfo set, not mid-decode),
  // start its separation exactly once.
  useEffect(() => {
    if (autoSepRef.current && trackInfo && !decoding && mode === 'input') {
      autoSepRef.current = false;
      skipAutoPlayRef.current = true; // no user gesture here → cue it paused, don't auto-play silently
      runSeparation(region.start, region.end);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackInfo, decoding, mode]);

  // "Replay in Studio" from the Sections page hands the section over via router
  // state; reopen it once the local stem-cache index is ready.
  useEffect(() => {
    if (!keysReady || reopenedRef.current) return;
    const st = location.state as { reopen?: Section; openTrack?: Section } | null;
    const reopen = st?.reopen;
    // "Continue where you left off" on the home page hands over a whole TRACK to
    // resume: YouTube songs re-open in the capture panel (re-grab a part),
    // uploads restore their cached stems straight into the mixer — mirroring the
    // Studio side rail's track behavior.
    const openTrack = st?.openTrack;
    if (!reopen && !openTrack) return;
    reopenedRef.current = true;
    if (openTrack) {
      const isUrl = openTrack.source_kind === 'url' || /^https?:\/\//i.test(openTrack.video_id || '');
      if (isUrl) openLinkTrack(openTrack);
      else reopenSection(openTrack);
    } else if (reopen) {
      reopenSection(reopen);
    }
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysReady]);

  useEffect(() => {
    const match = MIX_PRESETS.find((p) =>
      STEMS.every((s) => Math.abs(p.mix[s] - mix.stems[s].volume) < 0.02 && !mix.stems[s].solo && !mix.stems[s].mute)
    );
    setActivePreset(match?.id ?? null);
  }, [mix]);

  const rightsOk = !!user?.rights_acknowledged_at || hasAcknowledgedLocally();
  const stemColor = STEM_COLOR[chosenStem];

  function gateRights(action: () => void) {
    if (rightsOk) {
      action();
      return;
    }
    pendingAction.current = action;
    setRightsOpen(true);
  }

  // Decode the upload up front so the waveform + draggable region can render
  // before separation. Reused at isolate time (sliced) — no second decode.
  async function pickFile(f: File | null, linkSource: { videoId: string; url: string; title?: string } | null = null) {
    if (!f) {
      clearTrack();
      return;
    }
    setErrorMsg(null);
    linkSourceRef.current = linkSource;
    setFile(f);
    setDecoding(true);
    try {
      const dec = await decodeFileToStereo(f);
      decodedRef.current = dec;
      const peaks = computePeaks(dec.left, dec.right, 150);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const objectUrl = URL.createObjectURL(f);
      objectUrlRef.current = objectUrl;
      setTrackInfo({ name: f.name, size: f.size, durationSec: dec.durationSec, peaks, objectUrl });
      const end = Math.min(30, Math.max(1, dec.durationSec));
      setRegion({ start: 0, end });
      // A loaded track means separation is coming — warm the engine now so the
      // one-time setup overlaps with region-picking instead of following it.
      preloadEngine(sepEngine === 'roformer' && user?.plan === 'pro' ? 'roformer' : 'demucs');
      // Estimate key + BPM in the background — runs on the PCM we just decoded,
      // 100% client-side, and never blocks the track view appearing.
      setKeyBpm(null);
      setAnalyzingKb(true);
      detectKeyBpm(dec.left, dec.right, dec.sampleRate)
        .then(setKeyBpm)
        .catch(() => setKeyBpm(null))
        .finally(() => setAnalyzingKb(false));
    } catch {
      setErrorMsg('Could not read that audio file. Try a different format (MP3, WAV, M4A, FLAC, OGG).');
      setFile(null);
      decodedRef.current = null;
    } finally {
      setDecoding(false);
    }
  }

  function clearTrack() {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    decodedRef.current = null;
    linkSourceRef.current = null;
    setFile(null);
    setTrackInfo(null);
    setKeyBpm(null);
    setAnalyzingKb(false);
    setRegion({ start: 0, end: 30 });
    setMode('input');
    setErrorMsg(null);
  }

  // "From link" tab, step 2: the validated link opens an embedded player and
  // the audio is captured from this tab while it plays (see lib/ytCapture).
  // The captured file then drops into the exact same flow as an upload —
  // decode, waveform, region, on-device separation.
  function openCapture() {
    const raw = url.trim();
    const videoId = parseYtVideoId(raw);
    if (!videoId) {
      setErrorMsg('That doesn’t look like a YouTube link. Paste one like https://www.youtube.com/watch?v=…');
      return;
    }
    setErrorMsg(null);
    setCapturePanel({
      videoId,
      url: raw,
      title: linkMeta?.status === 'ok' ? linkMeta.title : 'YouTube track',
      author: linkMeta?.status === 'ok' ? linkMeta.author : '',
    });
  }

  // SoundCloud equivalent of openCapture: validate the pasted track link and
  // open the capture panel (the widget fetches title/artwork itself).
  function openScCapture(explicitUrl?: string) {
    const trackUrl = parseSoundCloudUrl((explicitUrl ?? scUrl).trim());
    if (!trackUrl) {
      setErrorMsg('That doesn’t look like a SoundCloud track link. Paste one like https://soundcloud.com/artist/track');
      return;
    }
    setErrorMsg(null);
    setScPanel({ trackUrl, title: 'SoundCloud track' });
  }

  // Core separation runner. The manual path (isolate button) passes the
  // waveform region; the one-run capture path passes the full captured window
  // (the capture start/length picker WAS the region choice) plus the fresh
  // File, because React state (`file`) hasn't re-rendered into this closure
  // by the time the capture hand-off calls in.
  async function runSeparation(startSec: number, endSec: number, srcFile?: File | null, verb: IntentVerb = chosenVerb) {
    const activeFile = srcFile ?? file;
    if (endSec <= startSec) {
      setErrorMsg('End time must be after start time.');
      return;
    }
    if (!activeFile || !decodedRef.current) {
      setErrorMsg('Choose an audio file first.');
      return;
    }

    setErrorMsg(null);

    // Quota gate — the free 5-per-24h cap is enforced server-side on the account.
    // Consume a loop BEFORE separating; if the account is out, block here instead
    // of letting the Studio separate for free. (Network hiccup → fail open so a
    // paying/free user isn't hard-blocked mid-session.)
    const link = linkSourceRef.current;
    try {
      const gate = await loopsApi.consume({
        source_kind: link ? 'url' : 'upload', stem: chosenStem,
        video_id: link ? link.videoId : activeFile.name,
        surface: 'studio', source_url: link ? link.url : '',
        quality: sepEngine === 'roformer' ? 'best' : 'standard',
      });
      if (gate && gate.exhausted) {
        usageApi.me().then(setUsageInfo).catch(() => {});
        setQuotaModal(gate.plan === 'anonymous' ? 'anon' : 'free'); // popup w/ Upgrade CTA
        return;
      }
    } catch { /* fail open */ }

    setMode('working');
    setProgress(0);
    setWorking({ step: 'separating', message: 'Separating audio…' });

    try {
      // CLIENT-SIDE separation: htdemucs_6s / BS-RoFormer via onnxruntime-web +
      // WebGPU in a Web Worker. We already have the decoded PCM — slice the
      // selected window and separate locally, then hand the stems to the mixer.
      const { left, right, sampleRate, durationSec } = decodedRef.current;
      const s0 = Math.max(0, Math.min(startSec, Math.max(0, durationSec - 0.1)));
      const s1 = Math.min(endSec, durationSec);
      const i0 = Math.floor(s0 * sampleRate);
      const i1 = Math.max(i0 + sampleRate, Math.floor(s1 * sampleRate)); // ≥1s
      const L = left.subarray(i0, i1);
      const R = right.subarray(i0, i1);
      const windowPeaks = computePeaks(L, R, 120);

      const { stemUrls, emptyStems, solos } = await separateClientSide(
        L,
        R,
        sampleRate,
        (frac) => setProgress(Math.round(frac * 100)),
        // Best (roformer) is Pro-only — defensively fall back to demucs for free
        // accounts so the Pro model can't be run via a stale selection.
        { engine: (sepEngine === 'roformer' && user?.plan !== 'pro') ? 'demucs' : sepEngine },
      );

      await loadStems({
        stemUrls,
        emptyStems,
        start: 0,
        end: (i1 - i0) / sampleRate,
        primaryStem: chosenStem,
        intent: verb,
        sourceKind: link ? 'url' : 'upload',
        sourceRef: link ? link.videoId : activeFile.name,
        sourceTitle: link?.title || cleanTrackName(activeFile.name),
        stemsCacheId: null,
        clientStemsKey: newSeparationKey(),
        peaks: windowPeaks,
        solos,
      });
      setCurrentSectionId(null); // a fresh separation isn't a saved section yet
      // (The loops.consume() gate above already recorded this separation for the
      // admin activity log AND advanced the quota window — don't double-log.)
      usageApi.me().then(setUsageInfo).catch(() => {});
      // Completed run: remember the intent (this is what makes the split CTA
      // self-personalizing) and honor a ?goal=karaoke arrival with the preset
      // that keeps the faint vocal guide.
      storeIntent(verb, chosenStem);
      firstRunRef.current = false;
      if (intentBootRef.current?.karaoke && verb === 'without' && chosenStem === 'vocals') {
        applyPreset('karaoke');
      }
    } catch (e) {
      handleErr(e);
    }
  }

  async function loadStems(l: Loaded) {
    setLoaded(l);
    await engine.load(l.stemUrls);
    engine.setWindow(l.start, l.end);

    // Funnel handoff entry brings the FULL mix up: the track is immediately
    // audible on the first Play (guaranteed sound even if the chosen stem is
    // silent in this window) and all six faders are there to explore. The
    // normal studio flow still isolates the chosen stem for practice.
    const fromHandoff = skipAutoPlayRef.current;
    const stems = Object.fromEntries(
      STEMS.map((s) => [
        s,
        {
          volume: fromHandoff
            ? 1
            : l.intent === 'without'
              ? s === l.primaryStem
                ? 0
                : 1
              : s === l.primaryStem
                ? 1
                : 0,
          solo: false,
          mute: false,
        },
      ]),
    ) as MixState['stems'];
    // Loop on by default so the section (and any trim of it) repeats automatically
    // for practice — the engine confines the loop to the active window/selection.
    // The Loop toggle still lets the user turn it off; reopened saved sections
    // override this with their own saved loop preference (see reopenSection).
    setMix({ ...DEFAULT_MIX, stems, loop: true });
    setMode('playing');
    setTime(0);
    setTrim({ start: 0, end: l.end - l.start }); // full clip; user can shrink it
    // When the separation was auto-started from a funnel handoff there was no
    // user gesture, so the AudioContext is suspended and play() would be silent
    // while still showing "Playing". Leave it cued/paused — the user's first
    // Play click resumes audio. The manual path (a real click) auto-plays.
    if (skipAutoPlayRef.current) {
      skipAutoPlayRef.current = false;
      setMix((m) => ({ ...m, playing: false }));
      return;
    }
    try {
      setMix((m) => ({ ...m, playing: true }));
      await engine.play();
    } catch {
      setMix((m) => ({ ...m, playing: false }));
    }
  }

  function handleErr(e: unknown) {
    const err = e as { code?: string; detail?: string };
    if (err?.code === 'anon_quota_exceeded') {
      setQuotaModal('anon');
      setMode('input');
      return;
    }
    if (err?.code === 'free_quota_exceeded') {
      setQuotaModal('free');
      setMode('input');
      return;
    }
    setErrorMsg(err?.detail || 'Something went wrong.');
    setMode('error');
  }

  function applyPreset(presetId: string) {
    const preset = MIX_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    setMix({
      ...mix,
      stems: Object.fromEntries(
        STEMS.map((s) => [s, { volume: preset.mix[s], solo: false, mute: false }])
      ) as MixState['stems'],
    });
  }

  // "+ New section" — keep the loaded track + region so the user can grab
  // another part; just tear down the playing engine and return to selection.
  function newSection() {
    const src = loaded; // captured before setLoaded(null) below
    engine.pause();
    engine.dispose();
    engineRef.current = new StemEngine();
    engineRef.current.setCallbacks({
      onTimeUpdate: (t) => setTime(t),
      onEnded: () => setMix((m) => ({ ...m, playing: false })),
    });
    setLoaded(null);
    setMix(DEFAULT_MIX);
    setMode('input');
    setWorking(null);
    setProgress(0);
    setErrorMsg(null);
    setCurrentSectionId(null);
    // "New loop" on a YouTube track should re-open the VIDEO to grab a new
    // section from the whole thing — not the little clip we already captured.
    // Drop the decoded clip (which otherwise shows its own tiny waveform) and
    // bring the embed's section-picker back. Uploads keep their full file, so
    // they fall through to the normal track panel.
    if (src && src.sourceKind === 'url' && src.sourceRef) {
      const ref = src.sourceRef;
      decodedRef.current = null;
      setFile(null);
      setTrackInfo(null);
      setKeyBpm(null);
      setAnalyzingKb(false);
      // SoundCloud refs are full track URLs; YouTube refs are bare video ids.
      const scTrack = parseSoundCloudUrl(ref);
      if (scTrack) {
        setScPanel((sp) => sp ?? { trackUrl: scTrack, title: src.sourceTitle || 'SoundCloud track' });
      } else {
        setCapturePanel((cp) => cp ?? {
          videoId: ref,
          url: 'https://www.youtube.com/watch?v=' + ref,
          title: src.sourceTitle || 'YouTube track',
          author: '',
        });
      }
    }
  }

  // "Start over" — full reset back to the empty upload screen: newSection() tears
  // down the playing engine + clears the result, clearTrack() drops the source
  // track/file/region. Reuses both tested resets so nothing is left dangling.
  function startOver() {
    newSection();
    clearTrack();
    setActivePreset(null);
    setCapturePanel(null);
    setScPanel(null);
    setScUrl('');
  }

  function togglePlay() {
    if (mix.playing) {
      engine.pause();
      setMix((m) => ({ ...m, playing: false }));
    } else {
      engine.play();
      setMix((m) => ({ ...m, playing: true }));
    }
  }

  // Double-click the result waveform => seek there and play from that point.
  // Clamped to the current trim window so you can't seek into trimmed-off audio.
  function seekTo(t: number) {
    const hi = trim.end || (loaded ? loaded.end - loaded.start : t);
    const ct = Math.max(trim.start, Math.min(hi, t));
    engine.seekAll(ct);
    setTime(ct);
    if (!mix.playing) {
      engine.play();
      setMix((m) => ({ ...m, playing: true }));
    }
  }

  // Move the playhead WITHOUT forcing playback (transport skip/restart buttons):
  // keeps the current play/pause state, just repositions. Clamped to the trim
  // window like seekTo.
  function seekOnly(t: number) {
    const hi = trim.end || (loaded ? loaded.end - loaded.start : t);
    const ct = Math.max(trim.start, Math.min(hi, t));
    engine.seekAll(ct);
    setTime(ct);
  }

  // ── Trim (keep a shorter version of the separated clip) ────────────────────
  // Dragging the handles on the result waveform live-bounds playback/loop so you
  // hear exactly what you'll keep (previewTrim); "Trim to m:ss" re-renders every
  // stem to that slice and reloads the engine, so Export + Save then operate on
  // the shorter clip with no other changes. Reset restores the full clip.
  function previewTrim(r: { start: number; end: number }) {
    setTrim(r);
    engine.setBounds(r.start, r.end);
  }
  function resetTrim() {
    if (!loaded) return;
    const full = { start: 0, end: loaded.end - loaded.start };
    setTrim(full);
    engine.setBounds(full.start, full.end);
  }
  async function commitTrim() {
    if (!loaded || trimming) return;
    const t0 = trim.start;
    const t1 = trim.end;
    const fullLen = loaded.end - loaded.start;
    if (t1 - t0 < 1 || t1 - t0 >= fullLen - 0.05) return; // nothing meaningful to cut
    setTrimming(true);
    const wasPlaying = mix.playing;
    try {
      engine.pause();
      // Re-render each present stem to the [t0,t1] slice → fresh WAV blob URLs,
      // and sum them into a downmix so we can RECOMPUTE the waveform at full
      // resolution. (Slicing the old 120-bar peaks array would leave only a few
      // fat bars when trimming to a small fraction of the clip.)
      const newUrls = { ...loaded.stemUrls } as Record<StemName, string>;
      let sumL: Float32Array | null = null;
      let sumR: Float32Array | null = null;
      for (const st of STEMS) {
        const url = loaded.stemUrls[st];
        if (!url) continue;
        const buf = await renderMix({ [st]: url }, { [st]: 1 }, { start: t0, end: t1 });
        newUrls[st] = URL.createObjectURL(audioBufferToWav(buf));
        const l = buf.getChannelData(0);
        const r = buf.numberOfChannels > 1 ? buf.getChannelData(1) : l;
        if (!sumL) {
          sumL = new Float32Array(l.length);
          sumR = new Float32Array(l.length);
        }
        const n = Math.min(sumL.length, l.length);
        for (let i = 0; i < n; i++) {
          sumL[i] += l[i];
          sumR![i] += r[i];
        }
      }
      const barCount = loaded.peaks.length || 120;
      const newPeaks = sumL ? computePeaks(sumL, sumR!, barCount) : loaded.peaks;
      const newLen = t1 - t0;
      await engine.load(newUrls);
      // The engine now plays off the new blobs, so free the previous stem URLs we
      // replaced (untouched stems keep theirs). Only after load resolves — never in
      // the catch path, where the old `loaded` (and its URLs) stays in use.
      for (const st of STEMS) {
        const prev = loaded.stemUrls[st];
        if (prev && prev !== newUrls[st]) URL.revokeObjectURL(prev);
      }
      engine.setWindow(0, newLen);
      engine.applyMix(mix);
      setLoaded({
        ...loaded,
        stemUrls: newUrls,
        start: 0,
        end: newLen,
        peaks: newPeaks,
        solos: [], // the clip's bounds changed — old solo bands no longer map
        clientStemsKey: newSeparationKey(), // a derived clip caches separately
      });
      setTrim({ start: 0, end: newLen });
      setTime(0);
      setCurrentSectionId(null); // a trimmed clip is a new result, not the saved one
      if (wasPlaying) {
        await engine.play();
        setMix((m) => ({ ...m, playing: true }));
      } else {
        setMix((m) => ({ ...m, playing: false }));
      }
    } catch (e) {
      setErrorMsg((e as { detail?: string; message?: string })?.detail || (e as Error)?.message || 'Trim failed.');
    } finally {
      setTrimming(false);
    }
  }

  // ── Export (fully client-side; the stems are already in the browser) ───────
  // Gating: Pro + Lifetime (both have plan==='pro') can export; FREE users see
  // the button and get an upgrade prompt; YouTube/link sources can't be exported
  // by anyone (legal — uploads only). A common entry runs the gate, then either
  // upsells, opens the download-rights modal, or runs the export.
  function exportFilename(target: 'mix' | StemName, format: ExportFormat): string {
    const base = safeBaseName(loaded?.sourceRef || '');
    const label = target === 'mix' ? 'Mix' : STEM_META[target]?.label || target;
    return `${base} - ${label}.${FORMAT_EXT[format]}`;
  }
  function requestExport(target: 'mix' | StemName, format: ExportFormat) {
    if (!loaded || !user) return;
    if (loaded.sourceKind !== 'upload') return; // YouTube — blocked (button is disabled too)
    if (user.plan !== 'pro') { setExportProOpen(true); return; } // free → upsell, no download
    if (!user.download_acknowledged_at) { pendingExport.current = { target, format }; setDownloadModalOpen(true); return; }
    void runExport(target, format);
  }
  async function runExport(target: 'mix' | StemName, format: ExportFormat) {
    if (!loaded || exporting) return;
    setExporting(true);
    try {
      let gains: Record<string, number>;
      if (target === 'mix') {
        const anySolo = Object.values(mix.stems).some((st) => st.solo);
        gains = {};
        for (const st of STEMS) {
          if (loaded.emptyStems.has(st)) continue;
          gains[st] = audibleVolume(mix.stems[st], anySolo);
        }
      } else {
        // A single stem at full level. Render through the same pipeline (rather than
        // downloading the raw blob) so the output is always a valid re-encoded file —
        // cache-reopened stems are Ogg-Opus and would otherwise be mislabelled.
        gains = { [target]: 1 };
      }
      const blob = await renderAndEncode(loaded.stemUrls, gains, format);
      saveBlob(blob, exportFilename(target, format));
    } catch (e) {
      setErrorMsg((e as { detail?: string; message?: string })?.detail || (e as Error)?.message || 'Export failed.');
    } finally {
      setExporting(false);
    }
  }

  function flashSaved() {
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 1600);
  }

  // Save button → if this result already maps to a saved section, ask whether
  // to update it or save a new one; otherwise just save a new one.
  function requestSave(nameOverride?: string) {
    if (!user || !loaded) return;
    // A name passed in (from an auto-detected "Guitar solo" chip) always saves a
    // fresh, named loop — skip the update-or-new dialog.
    if (!nameOverride && currentSectionId && savedSections.some((s) => s.id === currentSectionId)) {
      setSaveChoiceOpen(true);
    } else {
      saveAsNew(nameOverride);
    }
  }

  // Update the existing section's mix/loop in place (keeps its name + window).
  async function overrideSection() {
    setSaveChoiceOpen(false);
    if (!loaded || !user || !currentSectionId) return;
    const existing = savedSections.find((s) => s.id === currentSectionId);
    if (!existing) {
      saveAsNew();
      return;
    }
    const stemMixMap = Object.fromEntries(STEMS.map((s) => [s, mix.stems[s].volume]));
    try {
      await sectionsApi.sync([
        {
          id: existing.id,
          source_kind: existing.source_kind,
          video_id: existing.video_id,
          title: existing.title || loaded.sourceTitle || undefined,
          label: existing.label,
          stem: existing.stem,
          start: existing.start,
          end: existing.end,
          stem_mix: stemMixMap,
          loop: mix.loop,
        },
      ]);
      setSavedSections(await sectionsApi.list());
      flashSaved();
    } catch {
      /* surface in UI later */
    }
  }

  async function saveAsNew(nameOverride?: string) {
    setSaveChoiceOpen(false);
    if (!loaded || !user) return;
    // Free plan: max 3 saved loops per song (Pro/Lifetime unlimited). Editing an
    // existing loop goes through overrideSection, so this only caps brand-new ones.
    const songLoops = savedSections.filter(
      (s) => s.video_id === loaded.sourceRef && s.source_kind === loaded.sourceKind,
    ).length;
    if (user.plan !== 'pro' && songLoops >= 3) {
      setLoopCapModal(true);
      return;
    }
    const stemMixMap = Object.fromEntries(STEMS.map((s) => [s, mix.stems[s].volume]));
    const row: Partial<Section> = {
      source_kind: loaded.sourceKind,
      video_id: loaded.sourceRef,
      title: loaded.sourceTitle || undefined,
      label: nameOverride || STEM_META[loaded.primaryStem].label,
      stem: loaded.primaryStem,
      start: loaded.start,
      end: loaded.end,
      stem_mix: stemMixMap,
      loop: mix.loop,
    };
    const beforeIds = new Set(savedSections.map((sx) => sx.id));
    let created: Section | undefined;
    try {
      await sectionsApi.sync([row]);
      const list = await sectionsApi.list();
      created = list.find((sx) => !beforeIds.has(sx.id));
      setSavedSections(list); // section appears immediately
    } catch {
      return;
    }
    if (created) {
      setCurrentSectionId(created.id); // future saves can update this one
      flashSaved();
    }

    // Persist the stems locally (compressed to Opus, WAV fallback) so this
    // section re-opens after a reload. Runs in the background so saving stays
    // instant; stems from one separation are encoded once and shared by any
    // later sections of the same clip.
    const key = loaded.clientStemsKey;
    if (!created || !key) return;
    const createdId = created.id;
    const { stemUrls, emptyStems, peaks, end, sourceRef } = loaded;
    void (async () => {
      try {
        if (!(await getSeparation(key))) {
          const stems: Partial<Record<StemName, Blob>> = {};
          for (const st of STEMS) {
            const url = stemUrls[st];
            if (!url) continue;
            try {
              const wav = await fetch(url).then((r) => r.blob());
              const opus = await wavToOpus(wav);
              stems[st] = opus ?? wav;
            } catch {
              /* skip a stem that won't read back */
            }
          }
          // Also stash the original upload so reopening this section can restore
          // the full selection picker ("New section" → grab another part).
          await cacheSeparation(key, { stems, emptyStems: [...emptyStems], peaks, duration: end, sourceRef, source: file ?? undefined });
        }
        await linkSection(key, createdId);
        setSectionKeys((prev) => new Map(prev).set(createdId, key));
      } catch {
        /* best-effort cache; the section is still saved server-side */
      }
    })();
  }

  // Open a saved section: if its stems are cached locally, load them back into
  // the engine (full re-open, even after a reload); otherwise fall back to
  // recalling just the mix onto whatever is currently loaded.
  // If a reopened section cached its original audio, decode it back into the
  // track state so the selection picker works again — i.e. "New section" returns
  // to the waveform instead of the empty upload screen. Runs in the background
  // after the stems load so reopening stays instant; the picker is only needed
  // once the user actually clicks "New section".
  async function restoreSourceForPicker(rec: CachedSeparation) {
    // The source may live on this record or on a sibling record of the same song
    // (we store it once per song to avoid duplicating a large file).
    const source = rec.source || (await getSourceBlob(rec.sourceRef));
    if (!source) return;
    try {
      const name = rec.sourceRef || 'track';
      const srcFile = new File([source], name, { type: source.type || 'audio/mpeg' });
      const dec = await decodeFileToStereo(srcFile);
      decodedRef.current = dec;
      const peaks = computePeaks(dec.left, dec.right, 150);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const objectUrl = URL.createObjectURL(srcFile);
      objectUrlRef.current = objectUrl;
      setFile(srcFile);
      setTrackInfo({ name, size: srcFile.size, durationSec: dec.durationSec, peaks, objectUrl });
      setRegion({ start: 0, end: Math.min(30, Math.max(1, dec.durationSec)) });
    } catch {
      /* couldn't restore the source — picker just won't be available */
    }
  }

  // Clicking a saved YouTube clip in "continue where you left off" re-opens it
  // in the YouTube tab, landing straight on the capture panel with the video
  // already loaded (skips the paste/validate step), rather than restoring
  // cached stems into the upload-tab mixer.
  function openLinkTrack(sec: Section) {
    // SoundCloud tracks re-open in the SoundCloud tab's capture panel, same as
    // YouTube ones re-open the embed (video_id holds the full track URL).
    const scTrack = parseSoundCloudUrl(sec.video_id || '');
    if (scTrack) {
      newSection();
      clearTrack();
      setChosenStem((audibleStem(sec) as StemName) ?? chosenStem);
      setTab('soundcloud');
      setScUrl(scTrack);
      setScPanel({ trackUrl: scTrack, title: sec.title || 'SoundCloud track' });
      return;
    }
    const id = ytIdOf(sec.video_id || '');
    if (!id) {
      reopenSection(sec); // not a resolvable YouTube id — fall back to restore
      return;
    }
    newSection();
    clearTrack();
    setChosenStem((audibleStem(sec) as StemName) ?? chosenStem);
    setTab('url');
    const link = `https://www.youtube.com/watch?v=${id}`;
    setUrl(link); // keeps the input + "Different link" back-button consistent
    setCapturePanel({ videoId: id, url: link, title: sec.title || 'YouTube track', author: '' });
  }

  async function reopenSection(sec: Section) {
    // Keep the Upload/YouTube tab in sync with what we're reopening, so a
    // recalled YouTube isolation doesn't leave the "Upload file" tab lit.
    setTab(sec.source_kind === 'url' || /^https?:\/\//i.test(sec.video_id || '') ? 'url' : 'upload');
    setCurrentSectionId(sec.id); // saving now offers to update this section
    const key = sectionKeys.get(sec.id);
    if (key && loaded?.clientStemsKey === key) {
      recallSection(sec);
      return;
    }
    if (key) {
      const rec = await getSeparation(key);
      if (rec) {
        await loadStems({
          stemUrls: stemUrlsFrom(rec),
          emptyStems: new Set(rec.emptyStems),
          start: 0,
          end: rec.duration,
          primaryStem: audibleStem(sec),
          intent: 'isolate', // reopened sections restore their own saved mix below
          sourceKind: sec.source_kind,
          sourceRef: sec.video_id,
          sourceTitle: sec.title || undefined,
          stemsCacheId: null,
          clientStemsKey: key,
          peaks: rec.peaks,
          solos: [], // detection runs at separation time; reopened sections skip it
        });
        void restoreSourceForPicker(rec); // background: re-enable the picker
        if (sec.stem_mix) {
          const stems = Object.fromEntries(
            STEMS.map((s) => [s, { volume: sec.stem_mix?.[s] ?? 0, solo: false, mute: false }])
          ) as MixState['stems'];
          setMix((m) => ({ ...m, stems, loop: sec.loop, playing: true }));
        }
        return;
      }
    }
    recallSection(sec);
  }

  // Recall a saved section's mix onto the currently loaded stems. The audio
  // itself isn't stored, so this snaps the saved mix/loop back within a session
  // (after a fresh separation of the same part).
  function recallSection(sec: Section) {
    if (!loaded || !sec.stem_mix) return;
    const stems = Object.fromEntries(
      STEMS.map((s) => [s, { volume: sec.stem_mix?.[s] ?? 0, solo: false, mute: false }])
    ) as MixState['stems'];
    setMix((m) => ({ ...m, stems, loop: sec.loop, playing: true }));
    engine.play();
  }

  // Inline section rename (the pencil on each saved section). sync upserts by id.
  async function renameSection(sec: Section, label: string) {
    const trimmed = label.trim();
    if (!trimmed || trimmed === sec.label) return;
    setSavedSections((prev) => prev.map((x) => (x.id === sec.id ? { ...x, label: trimmed } : x)));
    try {
      await sectionsApi.sync([
        {
          id: sec.id,
          source_kind: sec.source_kind,
          video_id: sec.video_id,
          label: trimmed,
          stem: sec.stem,
          start: sec.start,
          end: sec.end,
          stem_mix: sec.stem_mix,
          loop: sec.loop,
        },
      ]);
    } catch {
      /* keep the optimistic value; a later list refresh reconciles */
    }
  }

  const primaryDur = loaded ? loaded.end - loaded.start : 0;
  const handleRightsAccepted = useCallback(() => {
    setRightsOpen(false);
    const a = pendingAction.current;
    pendingAction.current = null;
    a?.();
  }, []);

  return (
    <div className="st-root">
      <div className="st-bg" aria-hidden="true" />

      <RightsModal
        open={rightsOpen}
        onCancel={() => {
          setRightsOpen(false);
          pendingAction.current = null;
        }}
        onAccepted={handleRightsAccepted}
      />
      {quotaModal && <QuotaModal kind={quotaModal} onClose={() => setQuotaModal(null)} />}
      {loopCapModal && <LoopCapModal onClose={() => setLoopCapModal(false)} />}
      {exportProOpen && <ExportProModal onClose={() => setExportProOpen(false)} />}
      {bestProOpen && <BestQualityModal onClose={() => setBestProOpen(false)} />}
      <DownloadAttestationModal
        open={downloadModalOpen}
        onCancel={() => { setDownloadModalOpen(false); pendingExport.current = null; }}
        onAccepted={async () => {
          setDownloadModalOpen(false);
          const pe = pendingExport.current;
          pendingExport.current = null;
          if (pe) await runExport(pe.target, pe.format);
        }}
      />
      {saveChoiceOpen && (
        <SaveChoiceModal
          name={savedSections.find((sx) => sx.id === currentSectionId)?.label || 'this section'}
          onOverride={overrideSection}
          onNew={saveAsNew}
          onCancel={() => setSaveChoiceOpen(false)}
        />
      )}

      <div className="st-head">
        <div>
          <div className="st-title">Stem Separator</div>
          <div className="st-sub">Upload a track, grab a section, and pull any instrument out clean.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {(loaded || trackInfo || capturePanel || scPanel || mode === 'working') && (
            <button className="st-btn st-btn-ghost st-btn-startover" onClick={startOver} title="Clear everything and start a new track">
              <span aria-hidden="true" className="st-startover-ic">↺</span>
              Start over
            </button>
          )}
          <CreditsChip usage={usageInfo} />
        </div>
      </div>

      <div className="st-layout">
        <div className="st-panel">
          <div className="st-tabs">
            <div className="st-tabs-seg">
              <button className={'st-tab' + (tab === 'upload' ? ' active' : '')} onClick={() => setTab('upload')}>
                {Icons.upload({ size: 15 })} Upload file
              </button>
              <button className={'st-tab' + (tab === 'url' ? ' active' : '')} onClick={() => setTab('url')}>
                {/* Mono (currentColor) when idle; turns YouTube-red when the tab
                    is active — see .st-tab.active .st-ic-yt. Play triangle knocked
                    out via evenodd so it reads in either colour. */}
                <svg className="st-tab-ic st-ic-yt" width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="currentColor" fillRule="evenodd" clipRule="evenodd" d="M5.8 5.6h12.4a3.8 3.8 0 0 1 3.8 3.8v5.2a3.8 3.8 0 0 1-3.8 3.8H5.8A3.8 3.8 0 0 1 2 14.6V9.4a3.8 3.8 0 0 1 3.8-3.8ZM10 9.2v5.6l5-2.8z" />
                </svg>
YouTube
              </button>
              <button className={'st-tab' + (tab === 'soundcloud' ? ' active' : '')} onClick={() => setTab('soundcloud')}>
                <svg className="st-tab-ic st-ic-sc" width="19" height="19" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <rect x="2.5" y="11" width="1.5" height="6" rx="0.75" />
                  <rect x="5.4" y="9.2" width="1.5" height="7.8" rx="0.75" />
                  <rect x="8.3" y="7.6" width="1.5" height="9.4" rx="0.75" />
                  <path d="M11.6 8.4c.5-.3 1-.4 1.6-.4a4 4 0 0 1 3.96 3.5c.28-.1.58-.16.88-.16A2.98 2.98 0 0 1 21 17c0 .01-8.9 0-9.4 0a.5.5 0 0 1-.5-.5V8.86c0-.18.08-.35.24-.46z" />
                </svg>
SoundCloud
              </button>
            </div>
          </div>

          <div className="st-panel-pad">
            {mode === 'working' && working ? (
              <ProcessingCard
                stem={chosenStem}
                verb={chosenVerb}
                progress={progress}
                durLabel={fmtTime(region.end - region.start)}
                sourceName={file ? sourceLabel(file.name, 'upload') : url ? sourceLabel(url, 'url') : undefined}
                onCancel={newSection}
              />
            ) : mode === 'playing' && loaded ? (
              <>
                {errorMsg && <ErrorBanner message={errorMsg} onClose={() => setErrorMsg(null)} />}
                <ResultConsole
                loaded={loaded}
                mix={mix}
                setMix={setMix}
                time={time}
                primaryDur={primaryDur}
                onTogglePlay={togglePlay}
                onSeekPlay={seekTo}
                onSeek={seekOnly}
                onNewSection={newSection}
                loops={savedSections.filter((s) => s.video_id === loaded.sourceRef)}
                currentLoopId={currentSectionId}
                onRecallLoop={reopenSection}
                onPreset={applyPreset}
                activePreset={activePreset}
                trim={trim}
                onTrim={previewTrim}
                onCommitTrim={commitTrim}
                onResetTrim={resetTrim}
                trimming={trimming}
                onSaveSection={user ? requestSave : undefined}
                saved={savedFlash}
                hasSource={!!trackInfo}
                exportMode={loaded.sourceKind !== 'upload' ? 'youtube' : user?.plan === 'pro' ? 'ready' : 'free'}
                exportStems={STEMS.filter((st) => !loaded.emptyStems.has(st))}
                exportFormats={exportFormats}
                exporting={exporting}
                onExport={requestExport}
                />
              </>
            ) : (
              <>
                {!trackInfo ? (
                  tab === 'url' ? (
                    capturePanel ? (
                      <YtCapturePanel
                        info={capturePanel}
                        stem={chosenStem}
                        onStem={setChosenStem}
                        engine={sepEngine}
                        setEngine={setSepEngine}
                        isPro={user?.plan === 'pro'}
                        onLockedBest={() => setBestProOpen(true)}
                        verb={chosenVerb}
                        firstRun={firstRunRef.current}
                        deepKaraoke={intentBootRef.current?.source === 'deeplink' && !!intentBootRef.current?.karaoke}
                        decoding={decoding}
                        gate={gateRights}
                        onBack={() => setCapturePanel(null)}
                        onUpload={() => setTab('upload')}
                        warm={() => preloadEngine(sepEngine === 'roformer' && user?.plan === 'pro' ? 'roformer' : 'demucs')}
                        onCaptured={async (f, v) => {
                          const cp = capturePanel;
                          setChosenVerb(v);
                          await pickFile(f, { videoId: cp.videoId, url: cp.url, title: cp.title });
                          const dec = decodedRef.current;
                          if (!dec) {
                            // Decode failed — back to the link card, where the
                            // error banner explains.
                            setCapturePanel(null);
                            return;
                          }
                          // One-run flow: the capture window WAS the region
                          // choice, so separate it immediately with the chosen
                          // part. The panel stays mounted; the working card
                          // takes over the moment separation starts, and a
                          // cancelled run falls back to the manual waveform
                          // view. All six stems come out of the one run, so
                          // switching instruments later costs nothing.
                          setRegion({ start: 0, end: dec.durationSec });
                          runSeparation(0, dec.durationSec, f, v);
                        }}
                      />
                    ) : (
                      <LinkCard
                        url={url}
                        setUrl={setUrl}
                        meta={linkMeta}
                        busyLabel={decoding ? 'Reading your track…' : null}
                        onLoad={openCapture}
                      />
                    )
                  ) : tab === 'soundcloud' ? (
                    scPanel ? (
                      <ScCapturePanel
                        info={scPanel}
                        stem={chosenStem}
                        onStem={setChosenStem}
                        engine={sepEngine}
                        setEngine={setSepEngine}
                        isPro={user?.plan === 'pro'}
                        onLockedBest={() => setBestProOpen(true)}
                        verb={chosenVerb}
                        decoding={decoding}
                        gate={gateRights}
                        onBack={() => setScPanel(null)}
                        onUpload={() => setTab('upload')}
                        warm={() => preloadEngine(sepEngine === 'roformer' && user?.plan === 'pro' ? 'roformer' : 'demucs')}
                        onCaptured={async (f, v, title) => {
                          const sp = scPanel;
                          setChosenVerb(v);
                          await pickFile(f, { videoId: sp?.trackUrl || '', url: sp?.trackUrl || '', title });
                          const dec = decodedRef.current;
                          if (!dec) {
                            setScPanel(null);
                            return;
                          }
                          setRegion({ start: 0, end: dec.durationSec });
                          runSeparation(0, dec.durationSec, f, v);
                        }}
                      />
                    ) : (
                      <ScLinkCard
                        url={scUrl}
                        setUrl={setScUrl}
                        valid={!!parseSoundCloudUrl(scUrl.trim())}
                        busyLabel={decoding ? 'Reading your track…' : null}
                        onLoad={openScCapture}
                      />
                    )
                  ) : (
                    <UploadZone decoding={decoding} onPick={pickFile} />
                  )
                ) : (
                  <TrackPanel track={trackInfo} region={region} setRegion={setRegion} accent={stemColor} onClear={clearTrack} keyBpm={keyBpm} analyzingKb={analyzingKb} />
                )}

                {trackInfo && (
                  <>
                    <StemGrid value={chosenStem} onChange={setChosenStem} />
                    {errorMsg && <ErrorBanner message={errorMsg} onClose={() => setErrorMsg(null)} />}
                    <ActionArea
                      stem={chosenStem}
                      verb={chosenVerb}
                      durLabel={fmtTime(region.end - region.start)}
                      disabled={decoding}
                      firstRun={firstRunRef.current}
                      onProcess={(v) => {
                        setChosenVerb(v);
                        gateRights(() => runSeparation(region.start, region.end, null, v));
                      }}
                    />
                    <EnginePicker
                      engine={sepEngine}
                      setEngine={setSepEngine}
                      isPro={user?.plan === 'pro'}
                      onLockedBest={() => setBestProOpen(true)}
                    />
                  </>
                )}

                {mode === 'error' && <ErrorBanner message={errorMsg || 'Something went wrong.'} onClose={newSection} />}
                {!trackInfo && errorMsg && <ErrorBanner message={errorMsg} onClose={() => setErrorMsg(null)} />}
              </>
            )}
          </div>
        </div>

        <Sidebar
          sections={savedSections}
          currentRef={loaded ? loaded.sourceRef : null}
          onRecall={reopenSection}
          onOpenLink={openLinkTrack}
          onRename={renameSection}
          onRemove={async (id) => {
            try {
              await sectionsApi.remove(id);
            } catch {
              /* ignore */
            }
            unlinkSection(id).catch(() => {});
            setSectionKeys((prev) => {
              const next = new Map(prev);
              next.delete(id);
              return next;
            });
            setSavedSections(await sectionsApi.list().catch(() => []));
          }}
        />
      </div>

      <Footer />
    </div>
  );
}

/* ───────────────────────── header / credits ───────────────────────── */

function CreditsChip({ usage }: { usage: UsageSummary | null }) {
  if (!usage) return null;
  if (usage.daily_quota == null) {
    return (
      <div className="st-credits">
        {Icons.spark({ size: 15 })}
        <span>
          <b>Pro</b> · unlimited isolations
        </span>
      </div>
    );
  }
  // Show loops LEFT (not used) so it matches the extension's "X of 5 left" chip.
  const remaining = usage.remaining != null
    ? usage.remaining
    : Math.max(0, usage.daily_quota - usage.today);
  const pct = Math.min(100, Math.round((remaining / Math.max(1, usage.daily_quota)) * 100));
  // Out of loops → turn the chip into a direct upgrade CTA (the friction moment).
  if (remaining <= 0) {
    return (
      <Link className="st-credits empty st-credits-cta" to="/pricing" title="You're out of free loops — upgrade for unlimited">
        <span>
          <b>0</b> of {usage.daily_quota} left
        </span>
        <span className="st-credits-up">Upgrade →</span>
      </Link>
    );
  }
  // Amber when down to the last one; brand otherwise.
  const tone = remaining <= 1 ? ' low' : '';
  return (
    <div className={'st-credits' + tone}>
      <span>
        <b>{remaining}</b> of {usage.daily_quota} left
      </span>
      <span className="meter">
        <i style={{ width: `${pct}%` }} />
      </span>
    </div>
  );
}

/* ───────────────────────── upload / link ───────────────────────── */

function UploadZone({ decoding, onPick }: { decoding: boolean; onPick: (f: File | null) => void }) {
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div
      className={'st-drop' + (over ? ' over' : '')}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onPick(f);
      }}
    >
      <div className="st-drop-icon">{decoding ? <span className="st-spin" style={{ display: 'grid' }}>{Icons.loop({ size: 24 })}</span> : Icons.upload({ size: 26 })}</div>
      <h3>{decoding ? 'Reading your track…' : 'Drop an audio file to begin'}</h3>
      <p>MP3 · WAV · M4A · FLAC · OGG &nbsp;·&nbsp; up to 50&nbsp;MB</p>
      <div className="st-drop-actions">
        <button className="st-btn st-btn-primary" onClick={() => inputRef.current?.click()} disabled={decoding}>
          {Icons.file({ size: 16 })} Choose file
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="audio/*,.mp3,.wav,.m4a,.flac,.ogg"
        hidden
        onChange={(e) => onPick(e.target.files?.[0] ?? null)}
      />
    </div>
  );
}

function LinkCard({
  url,
  setUrl,
  meta,
  busyLabel,
  onLoad,
}: {
  url: string;
  setUrl: (v: string) => void;
  meta: LinkMeta | null;
  busyLabel: string | null;
  onLoad: () => void;
}) {
  const busy = !!busyLabel;
  const preview = meta?.status === 'ok' ? meta : null;
  const canLoad = !!url.trim() && meta?.status !== 'bad' && meta?.status !== 'validating';
  return (
    <div className={preview ? 'st-yt-has' : 'st-drop'}>
      {!preview && (
        <>
          <div className="st-drop-icon st-drop-icon-brand">
            {busy ? (
              <span className="st-spin" style={{ display: 'grid' }}>{Icons.loop({ size: 24 })}</span>
            ) : (
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="2" y="5.4" width="20" height="13.2" rx="4.4" fill="#FF0000" />
                <path d="M10 8.8v6.4l5.6-3.2z" fill="#fff" />
              </svg>
            )}
          </div>
          <h3>{busy ? busyLabel : 'Practice a song straight from YouTube'}</h3>
          <p>
            {busy
              ? 'Usually takes 10–20 seconds. You’ll get the full track — then pick the part to practice.'
              : 'Paste a link — pick a section, mute vocals or instruments, loop it.'}
          </p>
        </>
      )}
      <div className={preview ? 'st-link-input st-link-input-pill' : 'st-link-input'}>
        {preview && (
          <svg className="st-link-input-ic" width="17" height="17" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M6.5 9.5l3-3M7.5 4.6l1-1a2.3 2.3 0 013.3 3.3l-1 1M8.5 11.4l-1 1a2.3 2.3 0 01-3.3-3.3l1-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        )}
        <input
          type="url"
          placeholder="https://www.youtube.com/watch?v=…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !busy && canLoad) onLoad();
          }}
          className="rl-input"
          spellCheck={false}
          disabled={busy}
        />
        {preview && !busy && (
          <button type="button" className="st-link-clear" onClick={() => setUrl('')}>
            Clear
          </button>
        )}
      </div>

      {meta?.status === 'validating' && (
        <div className="st-ytprev-status">
          <span className="st-spin" style={{ display: 'grid' }}>{Icons.loop({ size: 14 })}</span>
          Validating link…
        </div>
      )}
      {meta?.status === 'bad' && (
        <div className="st-ytprev-status st-ytprev-status-bad">
          This video can’t be loaded — it may be private, removed or region-locked. Try another link.
        </div>
      )}

      {preview && (
        <div className="st-ytprev">
          <div className="st-ytprev-head">
            <div className="st-ytprev-thumbwrap">
              {preview.thumb ? (
                <img className="st-ytprev-thumb" src={preview.thumb} alt="" loading="lazy" />
              ) : (
                <div className="st-ytprev-thumb st-ytprev-thumb-empty" />
              )}
              <span className="st-ytprev-play" aria-hidden="true">
                <span className="st-ytprev-play-tri" />
              </span>
              {!!preview.durationSec && (
                <span className="st-ytprev-dur st-mono">{fmtTime(preview.durationSec)}</span>
              )}
            </div>
            <div className="st-ytprev-meta">
              <span className="st-ytprev-channel">
                <svg className="st-yt-glyph" viewBox="0 0 24 18" width="18" height="13" aria-hidden="true">
                  <rect x="0" y="0" width="24" height="18" rx="4.5" fill="#FF0033" />
                  <path d="M9.5 5l6 4-6 4z" fill="#fff" />
                </svg>
                {preview.author || 'YouTube'}
              </span>
              <b>{preview.title}</b>
              {(preview.views != null || preview.likes != null) && (
                <span className="st-ytprev-stats">
                  {preview.views != null && (
                    <span>
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      {compactCount(preview.views)} views
                    </span>
                  )}
                  {preview.likes != null && (
                    <span>
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 20.5S3.5 15 3.5 9.2A4.2 4.2 0 0 1 12 7a4.2 4.2 0 0 1 8.5 2.2C20.5 15 12 20.5 12 20.5z" />
                      </svg>
                      {compactCount(preview.likes)}
                    </span>
                  )}
                </span>
              )}
              {busy && (
                <span className="st-ytprev-busy">
                  <span className="st-spin" style={{ display: 'grid' }}>{Icons.loop({ size: 13 })}</span>
                  {busyLabel}
                </span>
              )}
            </div>
            {!busy && (
              <button className="st-btn st-btn-primary st-ytprev-cta" onClick={onLoad} disabled={!canLoad}>
                Continue <span style={{ fontSize: 13 }}>→</span>
              </button>
            )}
          </div>
        </div>
      )}

      {!preview && (
        <div className="st-drop-actions">
          {!busy && (
            <button className="st-btn st-btn-primary" onClick={onLoad} disabled={!canLoad}>
              Continue <span style={{ fontSize: 13 }}>→</span>
            </button>
          )}
        </div>
      )}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--st-line)', fontSize: 12.5, color: 'var(--st-text-faint)' }}>
        Prefer to stay on YouTube? The{' '}
        <a
          href="https://chromewebstore.google.com/detail/riffloop-ai-stem-splitter/meinmcdiijnhaljldocknpglaehbmbmd"
          target="_blank"
          rel="noreferrer"
          style={{ color: 'inherit', textDecoration: 'underline' }}
        >
          Riffloop extension
        </a>{' '}
        splits stems right on the video page.
      </div>
    </div>
  );
}

/* ───────────────────── YouTube capture panel ───────────────────── */

// Max A→B selection for a YouTube capture. Capture is realtime (the video has
// to actually play through the window), so an unbounded selection could run for
// hours — 5 minutes is the ceiling. Uploads have no such cap.
const MAX_CAPTURE_SEC = 300;

function YtCapturePanel({
  info,
  stem,
  onStem,
  engine,
  setEngine,
  isPro,
  onLockedBest,
  verb,
  firstRun,
  deepKaraoke,
  decoding,
  gate,
  warm,
  onBack,
  onUpload,
  onCaptured,
}: {
  info: { videoId: string; url: string; title: string; author: string };
  stem: StemName;
  onStem: (s: StemName) => void;
  engine: SepEngine;
  setEngine: (e: SepEngine) => void;
  isPro: boolean;
  onLockedBest: () => void;
  verb: IntentVerb;
  firstRun: boolean;
  deepKaraoke: boolean;
  decoding: boolean;
  gate: (action: () => void) => void;
  warm: () => void;
  onBack: () => void;
  onUpload: () => void;
  onCaptured: (f: File, v: IntentVerb) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YtPlayer | null>(null);
  const handleRef = useRef<CaptureHandle | null>(null);
  const [dur, setDur] = useState(0);
  // A→B selection over the video timeline, extension-style: same draggable
  // Waveform region the rest of the Studio uses, with decorative bars standing
  // in for audio we don't have yet. Initialised to the first 30s (tighter
  // selections separate cleaner AND capture faster).
  const [range, setRange] = useState<{ start: number; end: number } | null>(null);
  // We have no PCM to draw a real wave, but we DO control the embed — so the
  // selection is auditioned through the video itself: dragging a handle scrubs
  // the player to that point, and Preview loops A→B. Playhead tracks the embed.
  const [previewing, setPreviewing] = useState(false);
  const [playhead, setPlayhead] = useState(0);
  const rangeRef = useRef(range);
  rangeRef.current = range;
  const previewRef = useRef(false);
  const phaseRef = useRef<'setup' | CapturePhase>('setup');
  const lastSeekRef = useRef(0);
  const lastAuditRef = useRef(-1);
  const scrubStopRef = useRef(0);
  const [phase, setPhase] = useState<'setup' | CapturePhase>('setup');
  const [capturedSec, setCapturedSec] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [embedErr, setEmbedErr] = useState<string | null>(null);
  // Embed play/mute state (driven by the IFrame API state events) + edit buffers
  // for the A/B time inputs (null = show the canonical value, not a live edit).
  const [ytPlaying, setYtPlaying] = useState(false);
  const [ytMuted, setYtMuted] = useState(false);
  const [editA, setEditA] = useState<string | null>(null);
  const [editB, setEditB] = useState<string | null>(null);
  // YouTube's own "most replayed" heatmap + named chapters → one-tap loop picks.
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const supported = tabCaptureSupported();
  phaseRef.current = phase;
  previewRef.current = previewing;
  const ytPlayingRef = useRef(false);
  ytPlayingRef.current = ytPlaying;

  // Build the embed once per video; poll for the duration (it can lag onReady
  // by a beat while the player loads metadata).
  useEffect(() => {
    let disposed = false;
    let durTimer = 0;
    loadYouTubeAPI()
      .then((YTns) => {
        if (disposed || !hostRef.current) return;
        const p = new YTns.Player(hostRef.current, {
          videoId: info.videoId,
          width: '100%',
          height: '100%',
          playerVars: { rel: 0, playsinline: 1, origin: window.location.origin },
          events: {
            onReady: () => {
              if (disposed) return;
              playerRef.current = p;
              const poll = () => {
                if (disposed) return;
                const d = p.getDuration();
                if (d > 0) {
                  setDur(d);
                  setRange((r) => r ?? { start: 0, end: Math.min(30, d) });
                } else durTimer = window.setTimeout(poll, 250);
              };
              poll();
            },
            onError: (e) => {
              if (!disposed) setEmbedErr(embedErrorMessage(e.data));
            },
            // Keep the play/pause control in sync with the real embed state
            // (covers plays/pauses triggered inside the iframe itself).
            // YT.PlayerState: PLAYING=1, PAUSED=2, ENDED=0.
            onStateChange: (e) => {
              if (disposed) return;
              if (e.data === 1) setYtPlaying(true);
              else if (e.data === 2 || e.data === 0) setYtPlaying(false);
            },
          },
        });
      })
      .catch(() => {
        if (!disposed) setEmbedErr('YouTube couldn’t be reached. Check your connection and try again.');
      });
    return () => {
      disposed = true;
      clearTimeout(durTimer);
      handleRef.current?.cancel();
      try {
        playerRef.current?.destroy();
      } catch {
        /* iframe already torn down */
      }
      playerRef.current = null;
    };
  }, [info.videoId]);

  // Drive the playhead from the embed's own clock, and enforce the A→B loop
  // while previewing. Idle unless the player exists and we're still selecting
  // (during capture the recorder owns the player).
  useEffect(() => {
    if (!(dur > 0)) return;
    const id = window.setInterval(() => {
      const p = playerRef.current;
      if (!p || phaseRef.current !== 'setup') return;
      let t = 0;
      try {
        t = p.getCurrentTime();
      } catch {
        return;
      }
      setPlayhead(t);
      if (previewRef.current) {
        const r = rangeRef.current;
        if (r && (t >= r.end - 0.05 || t < r.start - 0.5)) {
          try {
            p.seekTo(r.start, true);
          } catch {
            /* player gone */
          }
        }
      }
    }, 100);
    return () => window.clearInterval(id);
  }, [dur]);

  function stopPlayback() {
    window.clearTimeout(scrubStopRef.current);
    lastAuditRef.current = -1;
    previewRef.current = false;
    setPreviewing(false);
    try {
      playerRef.current?.pauseVideo();
    } catch {
      /* ignore */
    }
  }

  // Cancel the A→B loop WITHOUT pausing (used when free-playing the video).
  function cancelLoop() {
    window.clearTimeout(scrubStopRef.current);
    previewRef.current = false;
    setPreviewing(false);
  }

  // ── Video controls: drive the embed through the IFrame API ──
  function ytTogglePlay() {
    const p = playerRef.current;
    if (!p) return;
    if (ytPlayingRef.current) {
      try { p.pauseVideo(); } catch { /* gone */ }
    } else {
      cancelLoop();
      try { p.playVideo(); } catch { /* gone */ }
      setYtPlaying(true);
    }
  }
  function ytToggleMute() {
    const p = playerRef.current;
    if (!p) return;
    const m = !ytMuted;
    try { m ? p.mute() : p.unMute(); } catch { /* gone */ }
    setYtMuted(m);
  }
  function ytRestart() {
    const p = playerRef.current;
    if (!p) return;
    cancelLoop();
    try { p.seekTo(0, true); p.playVideo(); } catch { /* gone */ }
    setYtPlaying(true);
  }

  // Parse "m:ss" or plain seconds; clamp to [0, dur]. Falls back on garbage.
  function parseTime(str: string, fallback: number): number {
    const s = str.trim();
    let sec = NaN;
    if (/^\d+:\d{1,2}$/.test(s)) {
      const [m, ss] = s.split(':');
      sec = Number(m) * 60 + Number(ss);
    } else if (/^\d+(\.\d+)?$/.test(s)) {
      sec = Number(s);
    }
    if (isNaN(sec)) return fallback;
    return Math.max(0, Math.min(dur > 0 ? dur : sec, sec));
  }
  const MIN_GAP = 1.5; // seconds
  function commitA() {
    const r = rangeRef.current;
    if (editA == null || !r) { setEditA(null); return; }
    const sec = parseTime(editA, r.start);
    setRange({ start: Math.min(sec, r.end - MIN_GAP), end: r.end });
    setEditA(null);
  }
  function commitB() {
    const r = rangeRef.current;
    if (editB == null || !r) { setEditB(null); return; }
    const sec = parseTime(editB, r.end);
    setRange({ start: r.start, end: Math.max(sec, r.start + MIN_GAP) });
    setEditB(null);
  }

  // Keyboard shortcuts — only while choosing the section (setup phase), and
  // never while typing in an input. Space = play/pause the embed; ←/→ slide the
  // whole A–B region; ↑/↓ grow/shrink via B; Shift = 5s steps instead of 1s.
  useEffect(() => {
    if (!(dur > 0)) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (phaseRef.current !== 'setup') return;
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        ytTogglePlay();
        return;
      }
      const r = rangeRef.current;
      if (!r) return;
      const step = e.shiftKey ? 5 : 1;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const d = e.key === 'ArrowLeft' ? -step : step;
        const w = r.end - r.start;
        const ns = Math.max(0, Math.min(dur - w, r.start + d));
        setRange({ start: ns, end: ns + w });
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const d = e.key === 'ArrowUp' ? step : -step;
        setRange({ start: r.start, end: Math.max(r.start + MIN_GAP, Math.min(dur, r.end + d)) });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // ytTogglePlay/etc. read the latest values via refs, so [dur] is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dur]);

  // Once the duration is known, ask YouTube for its "most replayed" heatmap +
  // named chapters and turn them into loop suggestions (best-effort; empty = no
  // chips). Cheap + cached server-side.
  useEffect(() => {
    if (!(dur > 0) || !info.videoId) return;
    let alive = true;
    fetchYtSuggestions(info.videoId, dur).then((s) => { if (alive) setSuggestions(s); });
    return () => { alive = false; };
  }, [dur, info.videoId]);

  // Apply a suggestion → set the A→B selection to it (clamped to the 5-min cap)
  // and audition it through the embed.
  function applySuggestion(s: Suggestion) {
    const D = dur > 0 ? dur : s.end;
    let start = Math.max(0, Math.min(s.start, D));
    let end = Math.min(D, Math.max(s.end, start + 1.5));
    if (end - start > MAX_CAPTURE_SEC) {
      // Center the cap on the hottest point so the peak stays inside.
      const peak = Math.max(start, Math.min(s.peak, end));
      start = Math.max(0, Math.min(peak - MAX_CAPTURE_SEC / 2, D - MAX_CAPTURE_SEC));
      end = start + MAX_CAPTURE_SEC;
    }
    setRange({ start, end });
    const p = playerRef.current;
    if (p) {
      previewRef.current = true;
      setPreviewing(true);
      try { p.seekTo(start, true); p.playVideo(); } catch { /* ignore */ }
    }
  }

  function togglePreview() {
    const p = playerRef.current;
    if (!p || !range) return;
    if (previewRef.current) {
      stopPlayback();
      return;
    }
    previewRef.current = true;
    setPreviewing(true);
    try {
      p.seekTo(range.start, true);
      p.playVideo();
    } catch {
      /* ignore */
    }
  }

  // Dragging a handle scrubs the embed to that edge so the user HEARS where A
  // and B land (no PCM to draw, but the video plays). Moving B auditions the
  // run-up to the end; moving A/whole plays from the start. When the drag
  // settles, it doesn't stop — it drops into the A→B loop so the selection
  // keeps playing (extension-style). Throttled + a min-move guard so we don't
  // re-seek the embed every frame (which would stutter the audio).
  function auditionRegion(nextRaw: { start: number; end: number }) {
    const prev = rangeRef.current;
    // True clip length — prefer the player's live duration (the `dur` state is
    // set once and can, rarely, hold a preliminary value); fall back to `dur`.
    let live = 0;
    try { live = playerRef.current?.getDuration() || 0; } catch { /* not ready */ }
    const D = Math.max(1, live > 0 ? live : dur);
    // Keep the state in sync so the waveform maps against the same length.
    if (live > 0 && Math.abs(live - dur) > 1) setDur(live);
    // Clamp both edges into [0, D] FIRST — the selection can never run past the
    // end of the video (that made B exceed the clip length and overflow the wave).
    const clamp = (n: number) => Math.max(0, Math.min(n, D));
    let next = { start: clamp(nextRaw.start), end: clamp(nextRaw.end) };
    // Enforce the 5-minute cap on whichever edge moved, staying inside [0, D].
    if (next.end - next.start > MAX_CAPTURE_SEC) {
      const movedEnd = !prev || Math.abs(next.end - prev.end) >= Math.abs(next.start - prev.start);
      next = movedEnd
        ? { start: next.start, end: Math.min(next.start + MAX_CAPTURE_SEC, D) }
        : { start: Math.max(0, next.end - MAX_CAPTURE_SEC), end: next.end };
    }
    setRange(next);
    const p = playerRef.current;
    if (!p || !prev) return;
    const dStart = Math.abs(next.start - prev.start);
    const dEnd = Math.abs(next.end - prev.end);
    const auditT = dEnd > dStart + 0.01 ? Math.max(next.start, next.end - 2.5) : next.start;
    const now = Date.now();
    if (now - lastSeekRef.current > 140 && Math.abs(auditT - lastAuditRef.current) > 0.25) {
      lastSeekRef.current = now;
      lastAuditRef.current = auditT;
      previewRef.current = false; // scrubbing — don't let the poll bounce us yet
      try {
        p.seekTo(auditT, true);
        p.playVideo();
      } catch {
        /* ignore */
      }
    }
    // Once they stop moving, loop the selection instead of stopping.
    window.clearTimeout(scrubStopRef.current);
    scrubStopRef.current = window.setTimeout(() => {
      previewRef.current = true;
      setPreviewing(true);
      try {
        playerRef.current?.playVideo();
      } catch {
        /* ignore */
      }
    }, 350);
  }

  const stemUi = STEM_UI.find((s) => s.id === stem)!;
  const windowSec = range ? Math.max(0, range.end - range.start) : 0;
  // Decorative timeline bars, deterministic per video so the strip doesn't
  // reshuffle between renders.
  const abBars = seededBars(90, info.videoId.split('').reduce((a, c) => a + c.charCodeAt(0), 7));
  const busy = phase !== 'setup' || decoding;
  const progressPct = windowSec > 0 ? Math.min(100, (capturedSec / windowSec) * 100) : 0;

  function begin(v: IntentVerb) {
    const p = playerRef.current;
    if (!p || dur <= 0 || !range || handleRef.current) return;
    if (range.end - range.start < 3) {
      setErr('Grab at least a few seconds — drag the B handle right.');
      return;
    }
    // Kick the engine warm-up now: the download + GPU setup runs while the
    // song plays, so separation can start the instant capture ends.
    warm();
    stopPlayback(); // hand the player to the capture engine cleanly
    setErr(null);
    setCapturedSec(0);
    const handle = startCapture(p, { start: range.start, end: range.end }, setPhase, setCapturedSec);
    handleRef.current = handle;
    handle.done
      .then((blob) => {
        if (!blob) return; // user cancelled
        onCaptured(new File([blob], `${info.title || 'YouTube track'}.webm`, { type: blob.type || 'audio/webm' }), v);
      })
      .catch((e: unknown) => {
        const msg = e instanceof CaptureError ? captureErrorMessage(e) : 'Recording failed. Try again.';
        if (msg) setErr(msg);
      })
      .finally(() => {
        handleRef.current = null;
        setPhase('setup');
      });
  }

  const phaseLabel = decoding
    ? 'Reading your track…'
    : phase === 'permission'
      ? 'In Chrome’s dialog: keep “This tab” selected and turn on “Also share tab audio”.'
      : phase === 'ad-wait'
        ? 'Waiting for the song to start — ads are kept out of the take.'
        : phase === 'paused'
          ? 'Capture paused — it resumes automatically when the song plays on.'
          : phase === 'finishing'
            ? 'Wrapping up the take…'
            : `Capturing — ${fmtTime(Math.min(capturedSec, windowSec))} of ${fmtTime(windowSec)}`;

  return (
    <div className="st-cap">
      <div className="st-cap-top">
        <button className="st-btn st-btn-ghost st-cap-back" onClick={onBack} disabled={busy}>
          <span aria-hidden="true" style={{ fontSize: 13, lineHeight: 1 }}>←</span> Different link
        </button>
        <div className="st-cap-title">
          <b>{info.title}</b>
          {info.author && <span>{info.author}</span>}
        </div>
      </div>

      <div className="st-cap-frame">
        <div ref={hostRef} />
        {embedErr && <div className="st-cap-embed-err">{embedErr}</div>}
      </div>

      {!supported && (
        <div className="st-cap-unsupported">
          <p className="st-cap-unsupported-txt">
            Grabbing audio from YouTube needs Chrome or Edge on a computer. On a phone, upload an audio file instead and
            everything else works the same.
          </p>
          <button className="st-btn st-btn-primary st-cap-unsupported-cta" onClick={onUpload}>
            {Icons.upload({ size: 15 })} Upload a file instead
          </button>
        </div>
      )}

      {supported && !embedErr && (
        busy ? (
          <div className="st-cap-live">
            <div className="st-cap-bar">
              <div className="st-cap-bar-fill" style={{ width: `${progressPct}%`, background: stemUi.color }} />
            </div>
            <div className="st-cap-phase">
              {phase === 'recording' && <span className="st-cap-dot" />}
              {phaseLabel}
            </div>
            {!decoding && (phase === 'recording' || phase === 'paused' || phase === 'ad-wait') && (
              <p className="st-cap-warn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                </svg>
                It’s recording in real time — leave this video playing. Don’t pause, seek, change speed, switch to another
                video, or stop sharing until it finishes.
              </p>
            )}
            <div className="st-drop-actions">
              {!decoding && phase !== 'finishing' && capturedSec >= 3 && (
                <button className="st-btn st-btn-soft" onClick={() => handleRef.current?.stop()}>
                  {Icons.check({ size: 15 })} Use what I’ve got
                </button>
              )}
              {!decoding && phase !== 'finishing' && (
                <button className="st-btn st-btn-ghost" onClick={() => handleRef.current?.cancel()}>Cancel</button>
              )}
            </div>
          </div>
        ) : dur > 0 && range ? (
          <div className="st-cap-setup">
            <div className="st-yt-ctls">
              <button className="st-yt-ctl" onClick={ytRestart} title="Restart" aria-label="Restart">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M3.5 9a5.5 5.5 0 105.5-5.5H6.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><path d="M8.2 1.4 6 3.5l2.2 2.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              <button className="st-yt-ctl st-yt-ctl-main" onClick={ytTogglePlay} title={ytPlaying ? 'Pause' : 'Play'} aria-label={ytPlaying ? 'Pause' : 'Play'}>
                {ytPlaying ? (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><rect x="4.5" y="3.5" width="4" height="13" rx="1.4" fill="currentColor" /><rect x="11.5" y="3.5" width="4" height="13" rx="1.4" fill="currentColor" /></svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ marginLeft: 3 }}><path d="M5 3.5v13L16.5 10z" fill="currentColor" /></svg>
                )}
              </button>
              <button className="st-yt-ctl" onClick={ytToggleMute} title={ytMuted ? 'Unmute' : 'Mute'} aria-label={ytMuted ? 'Unmute' : 'Mute'}>
                {ytMuted ? (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M2.5 6.5h2.7L9 3.3v11.4L5.2 11.5H2.5z" fill="currentColor" /><path d="M11.5 6.8l4.4 4.4M15.9 6.8l-4.4 4.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M2.5 6.5h2.7L9 3.3v11.4L5.2 11.5H2.5z" fill="currentColor" /><path d="M11.5 6.2a4 4 0 0 1 0 5.6M13.6 4.2a6.8 6.8 0 0 1 0 9.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                )}
              </button>
            </div>
            <div className="st-cap-head">
              <div className="st-lbl">Choose your section</div>
              <div className="st-field-hint">
                {windowSec >= MAX_CAPTURE_SEC ? '5-min max — split longer songs' : 'Drag the handles — shorter regions separate faster.'}
              </div>
            </div>
            <div className="st-cap-ab">
              <Waveform
                peaks={abBars}
                duration={dur}
                region={range}
                onRegion={auditionRegion}
                onSeekPlay={(t) => {
                  // Double-clicking the strip plays the selection from there.
                  const p = playerRef.current;
                  if (!p) return;
                  previewRef.current = true;
                  setPreviewing(true);
                  try {
                    p.seekTo(t, true);
                    p.playVideo();
                  } catch {
                    /* ignore */
                  }
                }}
                accent={stemUi.color}
                playing={previewing}
                playPos={playhead}
                height={88}
                axis={false}
                bands={suggestions.map((sg) => ({
                  startSec: sg.start,
                  endSec: sg.end,
                  color: sg.kind === 'heat' ? '#f59e0b' : 'var(--st-accent)',
                  label: sg.kind === 'heat' ? '🔥 Most replayed' : sg.label,
                }))}
              />
              <div className="st-cap-ab-times st-mono">
                <label className="st-ab-field">
                  A
                  <input
                    value={editA ?? fmtTime(range.start)}
                    onFocus={() => setEditA(fmtTime(range.start))}
                    onChange={(e) => setEditA(e.target.value)}
                    onBlur={commitA}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    spellCheck={false}
                    aria-label="Section start time"
                  />
                </label>
                <button
                  type="button"
                  className={'st-cap-preview' + (previewing ? ' on' : '')}
                  style={{ ['--st-stem' as string]: stemUi.color }}
                  onClick={togglePreview}
                  title="Loop just this A→B selection"
                >
                  {previewing ? '■ Stop · looping' : `▶ Preview loop · ${fmtTime(windowSec)}`}
                </button>
                <label className="st-ab-field">
                  B
                  <input
                    value={editB ?? fmtTime(range.end)}
                    onFocus={() => setEditB(fmtTime(range.end))}
                    onChange={(e) => setEditB(e.target.value)}
                    onBlur={commitB}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    spellCheck={false}
                    aria-label="Section end time"
                  />
                </label>
              </div>
              <div className="st-cap-shortcuts">
                <b>Space</b> play/pause · <b>← →</b> move section · <b>↑ ↓</b> resize · hold <b>Shift</b> for bigger steps
              </div>
              {suggestions.length > 0 && (
                <div className="st-sugg">
                  <span className="st-sugg-label">Jump to</span>
                  {suggestions.map((sg, i) => {
                    const hot = sg.kind === 'heat';
                    const glyph = hot ? '🔥' : /solo|guitar|riff/i.test(sg.label) ? '🎸' : '🎯';
                    return (
                      <button
                        key={i}
                        type="button"
                        className={'st-sugg-chip' + (hot ? ' hot' : '')}
                        onClick={() => applySuggestion(sg)}
                        title={`${sg.label} · ${fmtTime(sg.start)}–${fmtTime(sg.end)} — set the selection here`}
                      >
                        <span className="st-sugg-ic">{glyph}</span>
                        {hot ? 'Most replayed' : sg.label}
                        <span className="st-sugg-t st-mono">{fmtTime(sg.start)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="st-lbl" style={{ margin: '16px 0 10px' }}>Pick an instrument</div>
            <div className="st-stem-grid">
              {STEM_UI.map((s) => {
                const on = stem === s.id;
                return (
                  <button
                    key={s.id}
                    className={'st-stem' + (on ? ' on' : '')}
                    style={{ ['--st-stem' as string]: s.color }}
                    onClick={() => onStem(s.id)}
                    aria-pressed={on}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
            {err && <ErrorBanner message={err} onClose={() => setErr(null)} />}
            <div className="st-drop-actions">
              <SplitCta
                stem={stem}
                verb={verb}
                durLabel={fmtTime(windowSec)}
                onRun={(v) => gate(() => begin(v))}
              />
            </div>
            <EnginePicker engine={engine} setEngine={setEngine} isPro={isPro} onLockedBest={onLockedBest} />
            <p className="st-cap-hint">
              Chrome will ask to share this tab — keep <b>“This tab”</b> and turn on <b>“Also share tab audio”</b>.
            </p>
          </div>
        ) : (
          <div className="st-cap-note" style={{ textAlign: 'center' }}>
            Loading the video…
          </div>
        )
      )}
    </div>
  );
}

/* ───────────────────────── loaded track ───────────────────────── */

function TrackPanel({
  track,
  region,
  setRegion,
  accent,
  onClear,
  keyBpm,
  analyzingKb,
}: {
  track: TrackInfo;
  region: { start: number; end: number };
  setRegion: (r: { start: number; end: number }) => void;
  accent: string;
  onClear: () => void;
  keyBpm: KeyBpmResult | null;
  analyzingKb: boolean;
}) {
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(region.start);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const rafRef = useRef<number | null>(null);
  // The window currently being previewed: the selection (play button), or
  // [double-clicked point → end of track] (double-click on the waveform).
  const playWindowRef = useRef({ start: region.start, end: region.end });

  useEffect(() => {
    const a = new Audio(track.objectUrl);
    a.preload = 'auto';
    audioRef.current = a;
    return () => {
      a.pause();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      audioRef.current = null;
    };
  }, [track.objectUrl]);

  const tick = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.currentTime >= playWindowRef.current.end) {
      a.pause();
      setPlaying(false);
      setPos(playWindowRef.current.start);
      rafRef.current = null;
      return;
    }
    setPos(a.currentTime);
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const playFrom = useCallback(
    (start: number, end: number) => {
      const a = audioRef.current;
      if (!a) return;
      playWindowRef.current = { start, end };
      a.currentTime = start;
      setPos(start);
      a.play().catch(() => {});
      setPlaying(true);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(tick);
    },
    [tick],
  );

  const stop = useCallback(() => {
    const a = audioRef.current;
    if (a) a.pause();
    setPlaying(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  return (
    <div className="st-fade-up">
      <div className="st-track-head">
        <div className="st-track-thumb">{Icons.music({ size: 22 })}</div>
        <div className="st-track-meta">
          <div className="st-track-name">{cleanTrackName(track.name)}</div>
          <div className="st-track-sub st-mono">
            <span>{fmtTime(track.durationSec)}</span>
            <span>·</span>
            <span>{(track.size / (1024 * 1024)).toFixed(1)} MB</span>
            {analyzingKb && <span className="st-kb-analyzing">Analyzing key &amp; tempo…</span>}
            {keyBpm && (
              <span className="st-kb-pills">
                <span
                  className="st-kb-pill"
                  title={`Estimated key: ${keyBpm.keyName}${keyBpm.altKeyShort ? ` (or ${keyBpm.altKeyShort})` : ''}`}
                >
                  {keyBpm.keyConfidence < 0.18 && !keyBpm.altKeyShort ? '~' : ''}
                  {keyBpm.altKeyShort ? `${keyBpm.keyShort} / ${keyBpm.altKeyShort}` : keyBpm.keyShort}
                </span>
                {keyBpm.bpm > 0 && (
                  <span className="st-kb-pill" title="Estimated tempo">
                    {keyBpm.bpmConfidence < 0.25 ? '~' : ''}
                    {keyBpm.bpm} BPM
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
        <button className="st-icon-btn st-track-x" onClick={onClear} title="Remove">
          {Icons.x({ size: 16 })}
        </button>
      </div>

      <Waveform
        peaks={track.peaks}
        duration={track.durationSec}
        region={region}
        onRegion={setRegion}
        onSeekPlay={(t) => playFrom(t, track.durationSec)}
        accent={accent}
        playing={playing}
        playPos={pos}
      />

      <div className="st-transport">
        <button className="st-play-btn" onClick={() => (playing ? stop() : playFrom(region.start, region.end))}>
          {playing ? Icons.pause({ size: 18 }) : Icons.play({ size: 18 })}
        </button>
        <div className="st-time-pills">
          <div className="st-time-pill">
            <span>Start</span>
            <b className="st-mono">{fmtTime(region.start)}</b>
          </div>
          <div className="st-time-pill">
            <span>End</span>
            <b className="st-mono">{fmtTime(region.end)}</b>
          </div>
        </div>
        <div className="st-region-dur st-mono" style={{ ['--st-stem' as string]: accent }}>
          selection&nbsp; <b>{fmtTime(region.end - region.start)}</b>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────── SoundCloud capture (hidden tab) ──────────────────── */

// Mirrors YtCapturePanel, but plays a SoundCloud track through the SC Widget
// (wrapped as a YtPlayer) and captures this tab's audio the same way. No
// "most replayed" suggestions (SoundCloud has no heatmap). Reused verbatim:
// startCapture, the A/B Waveform, SplitCta, EnginePicker, the whole one-run flow.
function ScCapturePanel({
  info,
  stem,
  onStem,
  engine,
  setEngine,
  isPro,
  onLockedBest,
  verb,
  decoding,
  gate,
  warm,
  onBack,
  onUpload,
  onCaptured,
}: {
  info: { trackUrl: string; title: string };
  stem: StemName;
  onStem: (s: StemName) => void;
  engine: SepEngine;
  setEngine: (e: SepEngine) => void;
  isPro: boolean;
  onLockedBest: () => void;
  verb: IntentVerb;
  decoding: boolean;
  gate: (action: () => void) => void;
  warm: () => void;
  onBack: () => void;
  onUpload: () => void;
  onCaptured: (f: File, v: IntentVerb, title: string) => void;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<ScPlayer | null>(null);
  const handleRef = useRef<CaptureHandle | null>(null);
  const startedRef = useRef<string | null>(null);
  const [dur, setDur] = useState(0);
  const [range, setRange] = useState<{ start: number; end: number } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [playhead, setPlayhead] = useState(0);
  const rangeRef = useRef(range);
  rangeRef.current = range;
  const previewRef = useRef(false);
  const phaseRef = useRef<'setup' | CapturePhase>('setup');
  const lastSeekRef = useRef(0);
  const lastAuditRef = useRef(-1);
  const scrubStopRef = useRef(0);
  const [phase, setPhase] = useState<'setup' | CapturePhase>('setup');
  const [capturedSec, setCapturedSec] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [embedErr, setEmbedErr] = useState<string | null>(null);
  const [scPlaying, setScPlaying] = useState(false);
  const [scMuted, setScMuted] = useState(false);
  const [editA, setEditA] = useState<string | null>(null);
  const [editB, setEditB] = useState<string | null>(null);
  const [meta, setMeta] = useState<{ title: string; author: string; artwork: string } | null>(null);
  // False until we've observed the widget actually PLAYING once. The browser
  // blocks postMessage play() until the user has clicked inside the SoundCloud
  // iframe itself (our page's clicks don't cross the boundary) — so the first
  // play must happen on the widget's own ▶. Once seen, everything programmatic
  // works, including the recorder's play/seek.
  const [unlocked, setUnlocked] = useState(false);

  const supported = tabCaptureSupported();
  phaseRef.current = phase;
  previewRef.current = previewing;

  // Build the widget once per track; resolve with metadata (title/artwork/dur).
  // StrictMode double-invokes effects in dev — building the widget twice (and
  // the old teardown blanking iframe.src mid-load) left the SoundCloud widget in
  // a state where play() silently no-ops. So we guard to exactly one build per
  // track URL and DON'T tear the widget down on the fake unmount; a real unmount
  // removes the iframe (and the widget with it) anyway.
  useEffect(() => {
    const iframe = frameRef.current;
    if (!iframe) return;
    if (startedRef.current === info.trackUrl) return;
    startedRef.current = info.trackUrl;
    const url = info.trackUrl;
    const current = () => startedRef.current === url;
    setDur(0);
    setRange(null);
    setMeta(null);
    setEmbedErr(null);
    setUnlocked(false);
    playerRef.current = null;
    createScPlayer(iframe, url)
      .then((p) => {
        if (!current()) return;
        playerRef.current = p;
        setMeta({ title: p.title, author: p.author, artwork: p.artwork });
        const d = p.getDuration();
        if (d > 0) {
          setDur(d);
          setRange({ start: 0, end: Math.min(30, d) });
        } else {
          // Duration can lag the ready event by a beat — poll briefly.
          let tries = 0;
          const poll = () => {
            if (!current()) return;
            const dd = playerRef.current?.getDuration() ?? 0;
            if (dd > 0) {
              setDur(dd);
              setRange((r) => r ?? { start: 0, end: Math.min(30, dd) });
            } else if (tries++ < 20) window.setTimeout(poll, 250);
          };
          poll();
        }
      })
      .catch(() => {
        if (current()) setEmbedErr(scEmbedErrorMessage());
      });
    return () => {
      handleRef.current?.cancel();
    };
  }, [info.trackUrl]);

  // Playhead from the widget's own clock + A→B loop enforcement while
  // previewing; also mirrors the widget's play state onto the control button.
  useEffect(() => {
    if (!(dur > 0)) return;
    const id = window.setInterval(() => {
      const p = playerRef.current;
      if (!p) return;
      let t = 0;
      try {
        t = p.getCurrentTime();
        const playing = p.getPlayerState() === YT_STATE.PLAYING;
        setScPlaying(playing);
        // Real playback observed (clock moving) → the widget's audio is
        // unlocked; drop the "press play on the player" hint.
        if (playing && t > 0.05) setUnlocked(true);
      } catch {
        return;
      }
      if (phaseRef.current !== 'setup') return;
      setPlayhead(t);
      if (previewRef.current) {
        const r = rangeRef.current;
        if (r && (t >= r.end - 0.05 || t < r.start - 0.5)) {
          try {
            p.seekTo(r.start, true);
          } catch {
            /* gone */
          }
        }
      }
    }, 100);
    return () => window.clearInterval(id);
  }, [dur]);

  function stopPlayback() {
    window.clearTimeout(scrubStopRef.current);
    lastAuditRef.current = -1;
    previewRef.current = false;
    setPreviewing(false);
    try {
      playerRef.current?.pauseVideo();
    } catch {
      /* ignore */
    }
  }
  function cancelLoop() {
    window.clearTimeout(scrubStopRef.current);
    previewRef.current = false;
    setPreviewing(false);
  }
  function scTogglePlay() {
    const p = playerRef.current;
    if (!p) return;
    if (p.getPlayerState() === YT_STATE.PLAYING) {
      try { p.pauseVideo(); } catch { /* gone */ }
    } else {
      cancelLoop();
      try { p.playVideo(); } catch { /* gone */ }
    }
  }
  function scToggleMute() {
    const p = playerRef.current;
    if (!p) return;
    const m = !scMuted;
    try { m ? p.mute() : p.unMute(); } catch { /* gone */ }
    setScMuted(m);
  }
  function scRestart() {
    const p = playerRef.current;
    if (!p) return;
    cancelLoop();
    try { p.seekTo(0, true); p.playVideo(); } catch { /* gone */ }
  }

  function parseTime(str: string, fallback: number): number {
    const s = str.trim();
    let sec = NaN;
    if (/^\d+:\d{1,2}$/.test(s)) {
      const [m, ss] = s.split(':');
      sec = Number(m) * 60 + Number(ss);
    } else if (/^\d+(\.\d+)?$/.test(s)) {
      sec = Number(s);
    }
    if (isNaN(sec)) return fallback;
    return Math.max(0, Math.min(dur > 0 ? dur : sec, sec));
  }
  const MIN_GAP = 1.5;
  function commitA() {
    const r = rangeRef.current;
    if (editA == null || !r) { setEditA(null); return; }
    const sec = parseTime(editA, r.start);
    setRange({ start: Math.min(sec, r.end - MIN_GAP), end: r.end });
    setEditA(null);
  }
  function commitB() {
    const r = rangeRef.current;
    if (editB == null || !r) { setEditB(null); return; }
    const sec = parseTime(editB, r.end);
    setRange({ start: r.start, end: Math.max(sec, r.start + MIN_GAP) });
    setEditB(null);
  }

  useEffect(() => {
    if (!(dur > 0)) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (phaseRef.current !== 'setup') return;
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        scTogglePlay();
        return;
      }
      const r = rangeRef.current;
      if (!r) return;
      const step = e.shiftKey ? 5 : 1;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const d = e.key === 'ArrowLeft' ? -step : step;
        const w = r.end - r.start;
        const ns = Math.max(0, Math.min(dur - w, r.start + d));
        setRange({ start: ns, end: ns + w });
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const d = e.key === 'ArrowUp' ? step : -step;
        setRange({ start: r.start, end: Math.max(r.start + MIN_GAP, Math.min(dur, r.end + d)) });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dur]);

  function togglePreview() {
    const p = playerRef.current;
    if (!p || !range) return;
    if (previewRef.current) {
      stopPlayback();
      return;
    }
    previewRef.current = true;
    setPreviewing(true);
    try {
      p.seekTo(range.start, true);
      p.playVideo();
    } catch {
      /* ignore */
    }
  }

  function auditionRegion(nextRaw: { start: number; end: number }) {
    const prev = rangeRef.current;
    let live = 0;
    try { live = playerRef.current?.getDuration() || 0; } catch { /* not ready */ }
    const D = Math.max(1, live > 0 ? live : dur);
    if (live > 0 && Math.abs(live - dur) > 1) setDur(live);
    const clamp = (n: number) => Math.max(0, Math.min(n, D));
    let next = { start: clamp(nextRaw.start), end: clamp(nextRaw.end) };
    if (next.end - next.start > MAX_CAPTURE_SEC) {
      const movedEnd = !prev || Math.abs(next.end - prev.end) >= Math.abs(next.start - prev.start);
      next = movedEnd
        ? { start: next.start, end: Math.min(next.start + MAX_CAPTURE_SEC, D) }
        : { start: Math.max(0, next.end - MAX_CAPTURE_SEC), end: next.end };
    }
    setRange(next);
    const p = playerRef.current;
    if (!p || !prev) return;
    const dStart = Math.abs(next.start - prev.start);
    const dEnd = Math.abs(next.end - prev.end);
    const auditT = dEnd > dStart + 0.01 ? Math.max(next.start, next.end - 2.5) : next.start;
    const now = Date.now();
    if (now - lastSeekRef.current > 140 && Math.abs(auditT - lastAuditRef.current) > 0.25) {
      lastSeekRef.current = now;
      lastAuditRef.current = auditT;
      previewRef.current = false;
      try {
        p.seekTo(auditT, true);
        p.playVideo();
      } catch {
        /* ignore */
      }
    }
    window.clearTimeout(scrubStopRef.current);
    scrubStopRef.current = window.setTimeout(() => {
      previewRef.current = true;
      setPreviewing(true);
      try {
        playerRef.current?.playVideo();
      } catch {
        /* ignore */
      }
    }, 350);
  }

  const stemUi = STEM_UI.find((s) => s.id === stem)!;
  const windowSec = range ? Math.max(0, range.end - range.start) : 0;
  const abBars = seededBars(90, info.trackUrl.split('').reduce((a, c) => a + c.charCodeAt(0), 7));
  const busy = phase !== 'setup' || decoding;
  const progressPct = windowSec > 0 ? Math.min(100, (capturedSec / windowSec) * 100) : 0;
  const shownTitle = meta?.title || info.title || 'SoundCloud track';

  function begin(v: IntentVerb) {
    const p = playerRef.current;
    if (!p || dur <= 0 || !range || handleRef.current) return;
    if (range.end - range.start < 3) {
      setErr('Grab at least a few seconds — drag the B handle right.');
      return;
    }
    if (!unlocked) {
      setErr('First press play on the SoundCloud player above — one click there lets this page control it, then hit the button again.');
      return;
    }
    warm();
    stopPlayback();
    setErr(null);
    setCapturedSec(0);
    const handle = startCapture(p, { start: range.start, end: range.end }, setPhase, setCapturedSec);
    handleRef.current = handle;
    handle.done
      .then((blob) => {
        if (!blob) return;
        onCaptured(new File([blob], `${shownTitle}.webm`, { type: blob.type || 'audio/webm' }), v, shownTitle);
      })
      .catch((e: unknown) => {
        const msg = e instanceof CaptureError ? captureErrorMessage(e) : 'Recording failed. Try again.';
        if (msg) setErr(msg);
      })
      .finally(() => {
        handleRef.current = null;
        setPhase('setup');
      });
  }

  const phaseLabel = decoding
    ? 'Reading your track…'
    : phase === 'permission'
      ? 'In Chrome’s dialog: keep “This tab” selected and turn on “Also share tab audio”.'
      : phase === 'ad-wait'
        ? 'Waiting for the track to start…'
        : phase === 'paused'
          ? 'Capture paused — it resumes automatically when the track plays on.'
          : phase === 'finishing'
            ? 'Wrapping up the take…'
            : `Capturing — ${fmtTime(Math.min(capturedSec, windowSec))} of ${fmtTime(windowSec)}`;

  return (
    <div className="st-cap">
      <div className="st-cap-top">
        <button className="st-btn st-btn-ghost st-cap-back" onClick={onBack} disabled={busy}>
          <span aria-hidden="true" style={{ fontSize: 13, lineHeight: 1 }}>←</span> Different link
        </button>
        {meta?.artwork && <img className="st-cap-art" src={meta.artwork} alt="" loading="lazy" />}
        <div className="st-cap-title">
          <b>{shownTitle}</b>
          {meta?.author && <span>{meta.author}</span>}
        </div>
      </div>

      <div className="st-sc-frame">
        <iframe
          ref={frameRef}
          title="SoundCloud player"
          allow="autoplay"
          scrolling="no"
          frameBorder="no"
        />
        {embedErr && <div className="st-cap-embed-err">{embedErr}</div>}
      </div>

      {!supported && (
        <div className="st-cap-unsupported">
          <p className="st-cap-unsupported-txt">
            Grabbing audio from SoundCloud needs Chrome or Edge on a computer. On a phone, upload an audio file instead
            and everything else works the same.
          </p>
          <button className="st-btn st-btn-primary st-cap-unsupported-cta" onClick={onUpload}>
            {Icons.upload({ size: 15 })} Upload a file instead
          </button>
        </div>
      )}

      {supported && !embedErr && (
        busy ? (
          <div className="st-cap-live">
            <div className="st-cap-bar">
              <div className="st-cap-bar-fill" style={{ width: `${progressPct}%`, background: stemUi.color }} />
            </div>
            <div className="st-cap-phase">
              {phase === 'recording' && <span className="st-cap-dot" />}
              {phaseLabel}
            </div>
            {!decoding && (phase === 'recording' || phase === 'paused' || phase === 'ad-wait') && (
              <p className="st-cap-warn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                </svg>
                It’s recording in real time — leave this track playing. Don’t pause, seek, switch tabs, or stop sharing
                until it finishes.
              </p>
            )}
            <div className="st-drop-actions">
              {!decoding && phase !== 'finishing' && capturedSec >= 3 && (
                <button className="st-btn st-btn-soft" onClick={() => handleRef.current?.stop()}>
                  {Icons.check({ size: 15 })} Use what I’ve got
                </button>
              )}
              {!decoding && phase !== 'finishing' && (
                <button className="st-btn st-btn-ghost" onClick={() => handleRef.current?.cancel()}>Cancel</button>
              )}
            </div>
          </div>
        ) : dur > 0 && range ? (
          <div className="st-cap-setup">
            {!unlocked && (
              <div className="st-sc-unlock">
                <span className="st-sc-unlock-ic" aria-hidden="true">▶</span>
                <span>
                  <b>Press play on the SoundCloud player above once</b> — the browser needs one click on the player
                  itself before this page can drive it. After that, everything below works.
                </span>
              </div>
            )}
            <div className="st-yt-ctls">
              <button className="st-yt-ctl" onClick={scRestart} title="Restart" aria-label="Restart">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M3.5 9a5.5 5.5 0 105.5-5.5H6.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><path d="M8.2 1.4 6 3.5l2.2 2.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              <button className="st-yt-ctl st-yt-ctl-main" onClick={scTogglePlay} title={scPlaying ? 'Pause' : 'Play'} aria-label={scPlaying ? 'Pause' : 'Play'}>
                {scPlaying ? (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><rect x="4.5" y="3.5" width="4" height="13" rx="1.4" fill="currentColor" /><rect x="11.5" y="3.5" width="4" height="13" rx="1.4" fill="currentColor" /></svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ marginLeft: 3 }}><path d="M5 3.5v13L16.5 10z" fill="currentColor" /></svg>
                )}
              </button>
              <button className="st-yt-ctl" onClick={scToggleMute} title={scMuted ? 'Unmute' : 'Mute'} aria-label={scMuted ? 'Unmute' : 'Mute'}>
                {scMuted ? (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M2.5 6.5h2.7L9 3.3v11.4L5.2 11.5H2.5z" fill="currentColor" /><path d="M11.5 6.8l4.4 4.4M15.9 6.8l-4.4 4.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M2.5 6.5h2.7L9 3.3v11.4L5.2 11.5H2.5z" fill="currentColor" /><path d="M11.5 6.2a4 4 0 0 1 0 5.6M13.6 4.2a6.8 6.8 0 0 1 0 9.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                )}
              </button>
            </div>
            <div className="st-cap-head">
              <div className="st-lbl">Choose your section</div>
              <div className="st-field-hint">
                {windowSec >= MAX_CAPTURE_SEC ? '5-min max — split longer tracks' : 'Drag the handles — shorter regions separate faster.'}
              </div>
            </div>
            <div className="st-cap-ab">
              <Waveform
                peaks={abBars}
                duration={dur}
                region={range}
                onRegion={auditionRegion}
                onSeekPlay={(t) => {
                  const p = playerRef.current;
                  if (!p) return;
                  previewRef.current = true;
                  setPreviewing(true);
                  try {
                    p.seekTo(t, true);
                    p.playVideo();
                  } catch {
                    /* ignore */
                  }
                }}
                accent={stemUi.color}
                playing={previewing}
                playPos={playhead}
                height={88}
                axis={false}
              />
              <div className="st-cap-ab-times st-mono">
                <label className="st-ab-field">
                  A
                  <input
                    value={editA ?? fmtTime(range.start)}
                    onFocus={() => setEditA(fmtTime(range.start))}
                    onChange={(e) => setEditA(e.target.value)}
                    onBlur={commitA}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    spellCheck={false}
                    aria-label="Section start time"
                  />
                </label>
                <button
                  type="button"
                  className={'st-cap-preview' + (previewing ? ' on' : '')}
                  style={{ ['--st-stem' as string]: stemUi.color }}
                  onClick={togglePreview}
                  title="Loop just this A→B selection"
                >
                  {previewing ? '■ Stop · looping' : `▶ Preview loop · ${fmtTime(windowSec)}`}
                </button>
                <label className="st-ab-field">
                  B
                  <input
                    value={editB ?? fmtTime(range.end)}
                    onFocus={() => setEditB(fmtTime(range.end))}
                    onChange={(e) => setEditB(e.target.value)}
                    onBlur={commitB}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    spellCheck={false}
                    aria-label="Section end time"
                  />
                </label>
              </div>
              <div className="st-cap-shortcuts">
                <b>Space</b> play/pause · <b>← →</b> move section · <b>↑ ↓</b> resize · hold <b>Shift</b> for bigger steps
              </div>
            </div>
            <div className="st-lbl" style={{ margin: '16px 0 10px' }}>Pick an instrument</div>
            <div className="st-stem-grid">
              {STEM_UI.map((s) => {
                const on = stem === s.id;
                return (
                  <button
                    key={s.id}
                    className={'st-stem' + (on ? ' on' : '')}
                    style={{ ['--st-stem' as string]: s.color }}
                    onClick={() => onStem(s.id)}
                    aria-pressed={on}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
            {err && <ErrorBanner message={err} onClose={() => setErr(null)} />}
            <div className="st-drop-actions">
              <SplitCta
                stem={stem}
                verb={verb}
                durLabel={fmtTime(windowSec)}
                onRun={(v) => gate(() => begin(v))}
              />
            </div>
            <EnginePicker engine={engine} setEngine={setEngine} isPro={isPro} onLockedBest={onLockedBest} />
            <p className="st-cap-hint">
              Chrome will ask to share this tab — keep <b>“This tab”</b> and turn on <b>“Also share tab audio”</b>.
            </p>
          </div>
        ) : (
          <div className="st-cap-note" style={{ textAlign: 'center' }}>
            Loading the track…
          </div>
        )
      )}
    </div>
  );
}

// SoundCloud link input — paste a track URL, validate the shape, hand the
// canonical URL to the capture panel. (No live oEmbed preview: the widget
// itself surfaces the title/artwork once it loads.) Reuses the dropzone shell.
function ScLinkCard({
  url,
  setUrl,
  valid,
  busyLabel,
  onLoad,
}: {
  url: string;
  setUrl: (v: string) => void;
  valid: boolean;
  busyLabel: string | null;
  onLoad: (url?: string) => void;
}) {
  const busy = !!busyLabel;
  const inputRef = useRef<HTMLInputElement>(null);
  // Paste a valid link → load it immediately (like the YouTube tab reacts on
  // paste), no "Load track" click needed. Reads the value AFTER the browser
  // inserts the paste, so it works whether the field was empty or replaced.
  const onPaste = () => {
    window.setTimeout(() => {
      const v = (inputRef.current?.value || '').trim();
      if (!busy && parseSoundCloudUrl(v)) onLoad(v);
    }, 0);
  };
  return (
    <div className="st-drop">
      <div className="st-drop-icon st-drop-icon-brand">
        {busy ? (
          <span className="st-spin" style={{ display: 'grid' }}>{Icons.loop({ size: 24 })}</span>
        ) : (
          <svg width="34" height="34" viewBox="0 0 24 24" fill="#ff5500" aria-hidden="true">
            <rect x="2.5" y="11" width="1.5" height="6" rx="0.75" />
            <rect x="5.4" y="9.2" width="1.5" height="7.8" rx="0.75" />
            <rect x="8.3" y="7.6" width="1.5" height="9.4" rx="0.75" />
            <path d="M11.6 8.4c.5-.3 1-.4 1.6-.4a4 4 0 0 1 3.96 3.5c.28-.1.58-.16.88-.16A2.98 2.98 0 0 1 21 17c0 .01-8.9 0-9.4 0a.5.5 0 0 1-.5-.5V8.86c0-.18.08-.35.24-.46z" />
          </svg>
        )}
      </div>
      <h3>{busy ? busyLabel : 'Practice a track from SoundCloud'}</h3>
      <p>Paste a track link — pick a section, mute vocals or instruments, loop it.</p>
      <div className="st-link-input" style={{ marginTop: 4 }}>
        <input
          ref={inputRef}
          type="url"
          placeholder="https://soundcloud.com/artist/track"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onPaste={onPaste}
          onKeyDown={(e) => { if (e.key === 'Enter' && valid && !busy) onLoad(); }}
          className="rl-input"
          spellCheck={false}
          disabled={busy}
        />
      </div>
      <div className="st-drop-actions" style={{ marginTop: 12 }}>
        <button className="st-btn st-btn-stem" disabled={!valid || busy} onClick={() => onLoad()}>
          {Icons.link({ size: 15 })} Load track
        </button>
      </div>
      {url.trim() && !valid && (
        <p className="st-cap-note" style={{ marginTop: 10 }}>
          That doesn’t look like a SoundCloud track link. Use one like <b>soundcloud.com/artist/track</b> — single tracks only, not playlists.
        </p>
      )}
    </div>
  );
}

/* ───────────────────────── stem grid ───────────────────────── */

function StemGrid({ value, onChange }: { value: StemName; onChange: (s: StemName) => void }) {
  return (
    <div className="st-field">
      <div className="st-lbl" style={{ marginBottom: 10 }}>Pick an instrument</div>
      <div className="st-stem-grid">
        {STEM_UI.map((s) => {
          const on = value === s.id;
          return (
            <button
              key={s.id}
              className={'st-stem' + (on ? ' on' : '')}
              style={{ ['--st-stem' as string]: s.color }}
              onClick={() => onChange(s.id)}
              aria-pressed={on}
            >
              {s.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────────────────── engine + quality ───────────────────────── */

function EnginePicker({
  engine,
  setEngine,
  isPro,
  onLockedBest,
}: {
  engine: SepEngine;
  setEngine: (e: SepEngine) => void;
  isPro: boolean;
  onLockedBest: () => void;
}) {
  // Two real tiers: Standard = demucs (free), Best = BS-RoFormer (Pro). Picking
  // Best as a free user opens the upgrade popup instead of switching.
  const pickBest = () => { if (isPro) setEngine('roformer'); else onLockedBest(); };
  return (
    <div className="st-field">
      <div className="st-section-label">Separation quality</div>
      <div className="st-engine-row" style={{ marginTop: 13 }}>
        <button className={'st-engine' + (engine === 'demucs' ? ' on' : '')} onClick={() => setEngine('demucs')}>
          <div className="st-engine-top">
            <b>Standard</b>
          </div>
          <p>Fast — great on most songs</p>
          <div className="st-radio" />
        </button>
        <button className={'st-engine' + (engine === 'roformer' ? ' on' : '')} onClick={pickBest}>
          <div className="st-engine-top">
            <b>Best</b> <span className="st-tag st-tag-pro">Pro</span>
          </div>
          <p>Cleanest stems — especially piano &amp; vocals · a bit slower</p>
          {isPro ? (
            <div className="st-radio" />
          ) : (
            <span className="st-lock" aria-label="Pro only">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

/* ───────────────────────── action / processing ───────────────────────── */

/* ─────────────────── split-verb CTA (intent picker) ─────────────────── */

// One bar, two live verbs: the primary segment carries the last-used verb,
// the secondary mirrors it. BOTH run immediately — the user picks intent with
// the final click, so a wrong-intent run can't happen by accident.
function SplitCta({
  stem,
  verb,
  durLabel,
  disabled,
  onRun,
}: {
  stem: StemName;
  verb: IntentVerb;
  durLabel: string;
  disabled?: boolean;
  onRun: (v: IntentVerb) => void;
}) {
  const s = STEM_UI.find((x) => x.id === stem)!;
  const nameLc = STEM_META[stem].label.toLowerCase();
  const label = (v: IntentVerb) => (v === 'isolate' ? `Keep only ${nameLc}` : `Mute ${nameLc}`);
  const emoji = (v: IntentVerb) => (v === 'isolate' ? '🎧' : '🔇');
  const tip = (v: IntentVerb) => (v === 'isolate' ? `Hear ${nameLc} by itself` : `Hear the full mix minus ${nameLc}`);
  const alt: IntentVerb = verb === 'isolate' ? 'without' : 'isolate';
  return (
    <div className="st-actions2" style={{ ['--st-stem' as string]: s.color }}>
      <button className="st-act-primary" disabled={disabled} onClick={() => onRun(verb)} title={tip(verb)}>
        <span className="st-cta2-emoji">{emoji(verb)}</span> {label(verb)} · {durLabel}
      </button>
      <button className="st-act-secondary" disabled={disabled} onClick={() => onRun(alt)} title={tip(alt)}>
        <span className="st-cta2-emoji">{emoji(alt)}</span> {label(alt)}
      </button>
    </div>
  );
}

// One caption line under the chips. Priority: deep-link goal > vocals nudge >
// first-run reassurance > nothing.
function IntentCaption({
  stem,
  firstRun,
  deepKaraoke,
}: {
  stem: StemName;
  firstRun: boolean;
  deepKaraoke?: boolean;
}) {
  const text = deepKaraoke
    ? 'Karaoke-ready backing track'
    : stem === 'vocals'
      ? 'Without vocals → instant karaoke or backing track'
      : firstRun
        ? 'You get all six parts either way — this only sets your starting mix.'
        : null;
  if (!text) return null;
  return <p className="st-intent-note">{text}</p>;
}

function ActionArea({
  stem,
  verb,
  durLabel,
  disabled,
  firstRun,
  onProcess,
}: {
  stem: StemName;
  verb: IntentVerb;
  durLabel: string;
  disabled: boolean;
  firstRun: boolean;
  onProcess: (v: IntentVerb) => void;
}) {
  const s = STEM_UI.find((x) => x.id === stem)!;
  return (
    <div className="st-action">
      <IntentCaption stem={stem} firstRun={firstRun} />
      <SplitCta stem={stem} verb={verb} durLabel={durLabel} disabled={disabled} onRun={onProcess} />
      <div className="st-cta-foot">
        {disabled ? (
          <span>Reading track…</span>
        ) : (
          <>
            <span>{s.name}</span>
            <span className="dot" />
            <span>{durLabel}</span>
          </>
        )}
      </div>
    </div>
  );
}

function ProcessingCard({
  stem,
  verb,
  progress,
  durLabel,
  sourceName,
  onCancel,
}: {
  stem: StemName;
  verb: IntentVerb;
  progress: number;
  durLabel: string;
  sourceName?: string;
  onCancel: () => void;
}) {
  const s = STEM_UI.find((x) => x.id === stem)!;
  const name = STEM_META[stem].label;
  // <62% is engine warm-up (download + startup, first run only — warmed runs
  // jump past it) — say so instead of a vague "Preparing…".
  const stage = progress < 62 ? 'Setting up — first run takes a bit…' : progress < 95 ? 'Separating audio…' : 'Finishing up…';
  return (
    <div className="st-action" style={{ marginTop: 0 }}>
      <div className="st-proc st-fade-up" style={{ ['--st-stem' as string]: s.color }}>
        <div className="st-proc-top">
          <div className="st-proc-spin">
            <span className="st-proc-ring st-spin" />
          </div>
          <div style={{ minWidth: 0 }}>
            <b>{verb === 'without' ? `Removing ${name.toLowerCase()}…` : `Isolating ${name.toLowerCase()}…`}</b>
            <p style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={sourceName || undefined}>
              {sourceName ? `${sourceName} · ${stage}` : stage}
            </p>
          </div>
        </div>
        <div className="st-proc-bar">
          <i style={{ width: `${progress}%` }} />
        </div>
        <div className="st-proc-foot st-mono">
          <span>{durLabel} · {name}</span>
          <span className="pct">{Math.round(progress)}%</span>
        </div>
        <div className="st-proc-cancel">
          <button className="st-pill" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── result console ───────────────────────── */

function ResultConsole({
  loaded,
  mix,
  setMix,
  time,
  primaryDur,
  onTogglePlay,
  onSeekPlay,
  onSeek,
  onNewSection,
  loops,
  currentLoopId,
  onRecallLoop,
  onPreset,
  activePreset,
  onSaveSection,
  saved,
  hasSource,
  exportMode,
  exportStems,
  exportFormats,
  exporting,
  onExport,
  trim,
  onTrim,
  onCommitTrim,
  onResetTrim,
  trimming,
}: {
  loaded: Loaded;
  mix: MixState;
  setMix: (m: MixState) => void;
  time: number;
  primaryDur: number;
  onTogglePlay: () => void;
  onSeekPlay: (t: number) => void;
  onSeek: (t: number) => void;
  onNewSection: () => void;
  loops: Section[];
  currentLoopId: string | null;
  onRecallLoop: (sec: Section) => void;
  onPreset: (id: string) => void;
  activePreset: string | null;
  onSaveSection?: (name?: string) => void;
  saved: boolean;
  hasSource: boolean;
  exportMode: 'ready' | 'free' | 'youtube';
  exportStems: StemName[];
  exportFormats: ExportFormat[];
  exporting: boolean;
  onExport: (target: 'mix' | StemName, format: ExportFormat) => void;
  trim: { start: number; end: number };
  onTrim: (r: { start: number; end: number }) => void;
  onCommitTrim: () => void;
  onResetTrim: () => void;
  trimming: boolean;
}) {
  const s = STEM_UI.find((x) => x.id === loaded.primaryStem)!;
  const SPEED_PRESETS = [0.5, 0.75, 1, 1.25, 1.5];
  const [expMenu, setExpMenu] = useState(false);
  const [expFmt, setExpFmt] = useState<ExportFormat>('m4a'); // default M4A (small + universal, like Moises)
  // If the chosen format isn't offered (e.g. M4A on a non-WebCodecs browser), fall
  // back to the first available (MP3).
  useEffect(() => { if (!exportFormats.includes(expFmt)) setExpFmt(exportFormats[0]); }, [exportFormats, expFmt]);

  // Space toggles play/pause in the player (ignored while typing / in a field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== ' ' && e.key !== 'Spacebar') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      onTogglePlay();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onTogglePlay]);

  return (
    <div className="st-result st-fade-up" style={{ ['--st-stem' as string]: s.color }}>
      <div className="st-result-head">
        <div className="st-result-badge"><span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--st-stem)' }} /></div>
        <div style={{ flex: 1, minWidth: 160, overflow: 'hidden' }}>
          <b
            style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={loaded.sourceRef || undefined}
          >
            {loaded.sourceTitle?.trim() || sourceLabel(loaded.sourceRef, loaded.sourceKind)}
          </b>
          <p className="st-mono" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {mix.playing ? 'Playing' : 'Paused'} · {(loaded.intent === 'without' ? 'no ' : '') + STEM_META[loaded.primaryStem].label.toLowerCase()} · {fmtTime(time)} → {fmtTime(primaryDur)}
          </p>
        </div>
        <div className="st-result-actions">
          <button className="st-btn st-btn-stem" onClick={onTogglePlay}>
            {mix.playing ? Icons.pause({ size: 16 }) : Icons.play({ size: 16 })} {mix.playing ? 'Pause' : 'Play'}
          </button>
          <div className="st-exp-wrap">
            <button
              className="st-btn st-btn-ghost"
              onClick={() => {
                if (exportMode === 'youtube') return;
                if (exportMode === 'free') { onExport('mix', expFmt); return; } // → upgrade prompt
                setExpMenu((o) => !o);
              }}
              disabled={exportMode === 'youtube' || exporting}
              title={
                exportMode === 'youtube'
                  ? 'Streamed tracks can be practiced but not exported'
                  : exportMode === 'free'
                  ? 'Exporting is a Pro feature'
                  : 'Export the mix or individual stems'
              }
              style={exportMode === 'youtube' ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
            >
              {exporting ? (
                'Exporting…'
              ) : (
                <>
                  {Icons.download({ size: 16 })} Export
                  {exportMode !== 'ready' && <span className="st-tag st-tag-pro" style={{ marginLeft: 2 }}>Pro</span>}
                  {exportMode === 'ready' && (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 1 }}><path d="m6 9 6 6 6-6" /></svg>
                  )}
                </>
              )}
            </button>
            {expMenu && exportMode === 'ready' && (
              <>
                <div className="st-exp-scrim" onClick={() => setExpMenu(false)} />
                <div className="st-exp-menu" role="menu">
                  <div className="st-exp-fmts" role="group" aria-label="Export format">
                    {exportFormats.map((f) => (
                      <button
                        key={f}
                        className={'st-exp-fmtbtn' + (expFmt === f ? ' on' : '')}
                        onClick={() => setExpFmt(f)}
                        title={f === 'wav' ? 'Lossless · larger file' : f === 'm4a' ? 'AAC · small & high quality' : 'Smaller file · universal'}
                      >
                        {f.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  <button className="st-exp-item" onClick={() => { setExpMenu(false); onExport('mix', expFmt); }}>
                    {Icons.download({ size: 15 })} Full mix <span className="st-exp-fmt">.{FORMAT_EXT[expFmt]}</span>
                  </button>
                  {exportStems.length > 0 && <div className="st-exp-label">INDIVIDUAL STEMS</div>}
                  {exportStems.map((st) => (
                    <button key={st} className="st-exp-item" onClick={() => { setExpMenu(false); onExport(st, expFmt); }}>
                      <span className="st-exp-dot" style={{ background: STEM_COLOR[st] ?? 'var(--st-accent)' }} />
                      {STEM_META[st]?.label || st} <span className="st-exp-fmt">.{FORMAT_EXT[expFmt]}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <button
            className="st-btn st-btn-ghost"
            onClick={onNewSection}
            title={
              hasSource
                ? 'Keep this track loaded and go back to pick a different part to isolate. The current result is cleared (save it first to keep it). To drop the file entirely, use “Start over”.'
                : 'This result was reopened from your saved sections, so the original audio isn’t loaded — this goes to the upload screen. To shorten the current clip instead, use Trim above.'
            }
          >
            {Icons.plus({ size: 16 })} New loop
          </button>
        </div>
      </div>

      {/* Auto-detected section type(s) from the stem energies — a one-tap way to
          confirm "this clip is a guitar solo" and name the loop from it. */}
      {loaded.solos.length > 0 && (
        <div className="st-detected">
          {loaded.solos.map((sr, i) => {
            const col = STEM_COLOR[soloStem(sr.label)] ?? 'var(--st-accent)';
            return (
              <button
                key={i}
                type="button"
                className="st-detected-chip"
                style={{ ['--st-stem' as string]: col }}
                title={`Detected ${sr.label.toLowerCase()} · click to name this loop`}
                onClick={() => onSaveSection && onSaveSection(sr.label)}
              >
                <span className="st-detected-ic">{soloGlyph(sr.label)}</span>
                {sr.label}
                <span className="st-detected-hint">detected</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Loops in the main flow: this song's saved loops as recallable chips +
          a New loop button, so you don't have to reach for the side rail. */}
      <div className="st-loops">
        <div className="st-loops-head">
          <span className="st-section-label">Loops in this song</span>
          <span className="st-loops-count">{loops.length}</span>
          {onSaveSection && (
            <button className="st-loops-save" onClick={() => onSaveSection()}>
              {saved ? Icons.check({ size: 15 }) : Icons.save({ size: 15 })} {saved ? 'Saved' : 'Save loop'}
            </button>
          )}
        </div>
        <div className="st-loops-row">
          {loops.map((lp) => {
            const aud = audibleStem(lp);
            const color = STEM_COLOR[aud] ?? 'var(--st-accent)';
            const name = lp.label?.trim() || STEM_META[aud]?.label || 'Loop';
            const on = lp.id === currentLoopId;
            return (
              <button
                key={lp.id}
                className={'st-loop-chip' + (on ? ' on' : '')}
                style={{ ['--st-stem' as string]: color }}
                onClick={() => onRecallLoop(lp)}
                title={`${name} · ${fmtTime(lp.start)}–${fmtTime(lp.end)}`}
              >
                <span className="st-loop-chip-ic">{stemMark[aud] ? stemMark[aud](on ? '#fff' : color) : null}</span>
                <span className="st-loop-chip-tx">{name}</span>
                <span className="st-loop-chip-t st-mono">{fmtTime(lp.start)}</span>
              </button>
            );
          })}
          <button className="st-loop-chip st-loop-chip-new" onClick={onNewSection} title="Grab another part of this song">
            {Icons.plus({ size: 14 })} New loop
          </button>
        </div>
        {loops.length === 0 && (
          <p className="st-loops-hint">No loops saved yet — isolate a part and hit “Save loop” to keep it here.</p>
        )}
      </div>

      <Waveform
        peaks={loaded.peaks}
        duration={primaryDur}
        region={trim}
        onRegion={onTrim}
        onSeekPlay={onSeekPlay}
        accent={s.color}
        playing={mix.playing}
        playPos={time}
        height={84}
        axis={false}
        bands={loaded.solos.map((sr) => ({
          startSec: sr.startSec,
          endSec: sr.endSec,
          color: STEM_COLOR[soloStem(sr.label)] ?? 'var(--st-accent)',
          label: `${soloGlyph(sr.label)} ${sr.label}`,
        }))}
      />

      {/* Transport — playback controls sitting directly under the waveform:
          restart, skip ±5s, play/pause, and the running time. */}
      <div className="st-rtransport">
        <button className="st-rt-btn" title="Back to start" aria-label="Back to start" onClick={() => onSeek(trim.start)}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="5" y="5" width="2.4" height="14" rx="1.2" />
            <path d="M20 6.3a1 1 0 0 0-1.53-.85l-9 5.7a1 1 0 0 0 0 1.7l9 5.7A1 1 0 0 0 20 17.7z" />
          </svg>
        </button>
        <button className="st-rt-btn" title="Back 5 seconds" aria-label="Back 5 seconds" onClick={() => onSeek(time - 5)}>
          <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M11 6 5 12l6 6z" />
            <path d="M18.5 6l-6 6 6 6z" />
          </svg>
        </button>
        <button className="st-rt-play" title={mix.playing ? 'Pause' : 'Play'} aria-label={mix.playing ? 'Pause' : 'Play'} onClick={onTogglePlay}>
          {mix.playing ? Icons.pause({ size: 21 }) : Icons.play({ size: 21 })}
        </button>
        <button className="st-rt-btn" title="Forward 5 seconds" aria-label="Forward 5 seconds" onClick={() => onSeek(time + 5)}>
          <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M13 6l6 6-6 6z" />
            <path d="M5.5 6l6 6-6 6z" />
          </svg>
        </button>
        <div style={{ flex: 1 }} />
        <div className="st-rt-time st-mono">
          <b>{fmtTime(time)}</b> / {fmtTime(primaryDur)}
        </div>
      </div>

      {/* Trim — drag the handles to shorten the clip from either end, then keep
          just that range. Export + Save use whatever clip is currently loaded. */}
      {(() => {
        const len = trim.end - trim.start;
        const isFull = len >= primaryDur - 0.05;
        return (
          <div className="st-controls" style={{ marginTop: 10, alignItems: 'center', gap: 10 }}>
            <span className="st-ctl-label" style={{ marginRight: 2 }}>Trim</span>
            <span className="st-mono" style={{ fontSize: 12, color: 'var(--st-text-faint)' }}>
              {fmtTime(trim.start)} → {fmtTime(trim.end)}
            </span>
            <span className="st-mono" style={{ fontSize: 12, fontWeight: 700, color: isFull ? 'var(--st-text-faint)' : s.color }}>
              {fmtTime(len)}
            </span>
            {isFull && (
              <span style={{ color: 'var(--st-text-faint)', fontSize: 11 }}>
                drag the handles on the waveform to shorten
              </span>
            )}
            <div style={{ flex: 1 }} />
            {!isFull && (
              <button className="st-pill" onClick={onResetTrim} disabled={trimming}>
                Reset
              </button>
            )}
            <button
              className="st-btn st-btn-stem"
              onClick={onCommitTrim}
              disabled={isFull || trimming}
              title={isFull ? 'Drag the handles on the waveform to select a shorter range' : 'Keep only the selected range'}
            >
              {Icons.check({ size: 15 })} {trimming ? 'Trimming…' : `Trim to ${fmtTime(len)}`}
            </button>
          </div>
        );
      })()}

      {/* Full 6-stem mixer — solo/mute + faders. */}
      <div className="st-block">
        <div className="st-ctl-label" style={{ marginBottom: 8 }}>
          Mixer
        </div>
        <div className="st-mixer-box">
          <Mixer mix={mix} emptyStems={loaded.emptyStems} activeStem={loaded.primaryStem} onChange={setMix} />
        </div>
      </div>

      {/* Quick mixes + loop */}
      <div className="st-block">
        <div className="st-controls">
          <span className="st-ctl-label" style={{ marginRight: 4 }}>
            Quick mix
          </span>
          {MIX_PRESETS.map((p) => (
            <button key={p.id} className="st-pill" aria-pressed={activePreset === p.id} onClick={() => onPreset(p.id)}>
              {p.label}
            </button>
          ))}
          {loaded.intent === 'without' && loaded.primaryStem === 'vocals' && activePreset !== 'karaoke' && (
            <button
              className="st-pill st-pill-nudge"
              onClick={() => onPreset('karaoke')}
              title="Keep a quiet vocal guide to sing along with"
            >
              {Icons.spark({ size: 13 })} Add faint vocal guide
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button
            className="st-pill loop"
            aria-pressed={mix.loop}
            onClick={() => setMix({ ...mix, loop: !mix.loop })}
          >
            {Icons.loop({ size: 14 })} Loop {mix.loop ? 'on' : 'off'}
          </button>
        </div>
      </div>

      {/* Transpose + speed */}
      <div className="st-block">
        <div className="st-controls" style={{ gap: 10 }}>
          <span className="st-ctl-label">Transpose</span>
          <button className="st-mini-btn" onClick={() => setMix({ ...mix, transpose: Math.max(-12, mix.transpose - 1) })}>
            −
          </button>
          <div
            className="st-mono"
            style={{
              minWidth: 30,
              textAlign: 'center',
              fontWeight: 500,
              fontSize: 14,
              color: 'var(--st-text)',
            }}
          >
            {mix.transpose >= 0 ? '+' : ''}
            {mix.transpose}
          </div>
          <button className="st-mini-btn" onClick={() => setMix({ ...mix, transpose: Math.min(12, mix.transpose + 1) })}>
            +
          </button>
          <span style={{ color: 'var(--st-text-3)', fontSize: 12 }}>semitones</span>
          <span style={{ width: 16 }} />
          <span className="st-ctl-label">Speed</span>
          {SPEED_PRESETS.map((sp) => (
            <button
              key={sp}
              className="st-pill"
              aria-pressed={mix.speed === sp}
              onClick={() => setMix({ ...mix, speed: sp })}
              style={{ padding: '7px 13px', fontSize: 13 }}
            >
              {sp}×
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── sidebar ───────────────────────── */

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function MiniBars({ color, seed }: { color: string; seed: number }) {
  const bars = seededBars(26, seed);
  return (
    <div className="st-sec-bars" style={{ ['--st-stem' as string]: color }}>
      {bars.map((v, i) => (
        <i key={i} style={{ height: Math.max(3, v * 22) }} />
      ))}
    </div>
  );
}

// Display name for a whole TRACK (the source audio), from its video_id —
// mirrors the home page's song-name logic (strip extension, underscores → spaces).
function trackName(s: Section): string {
  // Prefer the stored source title (the real song name), captured on save.
  if (s.title && s.title.trim()) return s.title.trim();
  const v = s.video_id || '';
  if (s.source_kind === 'url' || /^https?:\/\//i.test(v)) return 'YouTube clip';
  const base = v.replace(/\.[a-z0-9]{1,5}$/i, '').replace(/_+/g, ' ').trim();
  return base || s.label || 'Untitled';
}

// Loudest >0.02 stem of a saved section's mix — the ACTUALLY-audible stem.
// NEVER sec.stem alone: picking "Vocals" on an instrumental auto-swaps the
// audible stem, so sec.stem can read 'vocals' while the content is Solo & Lead.
function audibleStem(sec: Section): StemName {
  const a = STEMS
    .map((st) => ({ st, v: sec.stem_mix?.[st] ?? (st === (sec.stem as StemName) ? 1 : 0) }))
    .filter((x) => x.v > 0.02)
    .sort((x, y) => y.v - x.v);
  return (a[0]?.st as StemName) ?? (sec.stem as StemName);
}

// Human-readable name for the source being worked on (result console + the
// processing card), from an upload filename or a link. Mirrors trackName:
// strips the extension and turns underscores into spaces.
function sourceLabel(ref: string, kind: 'url' | 'upload'): string {
  if (kind === 'url' || /^https?:\/\//i.test(ref || '')) return 'YouTube clip';
  const base = (ref || '').replace(/\.[a-z0-9]{1,5}$/i, '').replace(/_+/g, ' ').trim();
  return base || 'Untitled';
}

// Drop the file extension for display — captured YouTube tracks are named
// "<video title>.webm", uploads "<name>.mp3", and the extension is just noise
// in the header.
function cleanTrackName(name: string): string {
  return name.replace(/\.(webm|mp3|wav|m4a|flac|ogg|opus|aac|mp4|mov)$/i, '').trim() || name;
}

// A section's YouTube video id, whether video_id holds a bare id or a URL.
function ytIdOf(videoId: string): string | null {
  if (/^[\w-]{6,15}$/.test(videoId)) return videoId;
  return parseYtVideoId(videoId);
}

function Sidebar({
  sections,
  currentRef,
  onRecall,
  onOpenLink,
  onRename,
  onRemove,
}: {
  sections: Section[];
  currentRef: string | null;
  onRecall: (sec: Section) => void;
  onOpenLink: (sec: Section) => void;
  onRename: (sec: Section, label: string) => void;
  onRemove: (id: string) => void;
}) {
  const renderSec = (sec: Section) => {
    const aud = audibleStem(sec);
    const color = STEM_COLOR[aud] ?? 'var(--st-accent)';
    const mark = stemMark[aud];
    const name = sec.label?.trim() || STEM_META[aud]?.label || 'Section';
    return (
      <div
        className="st-sec"
        key={sec.id}
        style={{ ['--st-stem' as string]: color }}
        role="button"
        tabIndex={0}
        title="Recall this mix"
        onClick={() => onRecall(sec)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onRecall(sec);
          }
        }}
      >
        <div className="st-sec-ic" style={{ background: `color-mix(in oklch, ${color} 16%, transparent)` }}>
          {mark ? mark(color) : Icons.music({ size: 18 })}
        </div>
        <div className="st-sec-mini">
          <b>
            <EditableName value={name} color={color} onSave={(v) => onRename(sec, v)} title={`Rename ${name}`} />
          </b>
          <span className="st-mono">
            {fmtTime(sec.start)}–{fmtTime(sec.end)}
          </span>
        </div>
        <MiniBars color={color} seed={hashSeed(sec.id)} />
        <button
          className="st-sec-x"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(sec.id);
          }}
          aria-label="Remove section"
          title="Remove"
        >
          {Icons.x({ size: 14 })}
        </button>
      </div>
    );
  };

  // A whole TRACK card (the source audio you last worked on) — distinct from a
  // saved stem section. Resumes that track + its most-recent mix on click.
  const renderTrack = (sec: Section) => {
    const name = trackName(sec);
    const isUrl = sec.source_kind === 'url' || /^https?:\/\//i.test(sec.video_id || '');
    const isSc = /(^|\.)soundcloud\.com\//i.test(sec.video_id || '');
    const ytId = isUrl && !isSc ? ytIdOf(sec.video_id || '') : null;
    const count = sections.filter((s) => s.video_id === sec.video_id && s.source_kind === sec.source_kind).length;
    const accent = 'var(--st-accent)';
    // YouTube clips re-open fresh in the YouTube tab (re-capture); uploads
    // restore their cached stems straight into the mixer.
    const open = () => (isUrl ? onOpenLink(sec) : onRecall(sec));
    return (
      <div
        className="st-sec"
        style={{ ['--st-stem' as string]: accent }}
        role="button"
        tabIndex={0}
        title={isUrl ? 'Open this video again' : 'Resume this track'}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
          }
        }}
      >
        {ytId ? (
          <img className="st-sec-thumb" src={`https://i.ytimg.com/vi/${ytId}/mqdefault.jpg`} alt="" loading="lazy" />
        ) : (
          <div className="st-sec-ic" style={{ background: `color-mix(in oklch, ${accent} 16%, transparent)` }}>
            {Icons.music({ size: 18 })}
          </div>
        )}
        <div className="st-sec-mini">
          <b>{name}</b>
          <span className="st-mono">
            {isUrl ? (isSc ? 'SoundCloud clip' : 'YouTube clip') : 'Upload'} · {count} section{count === 1 ? '' : 's'}
          </span>
        </div>
        <MiniBars color={accent} seed={hashSeed(sec.video_id || sec.id)} />
      </div>
    );
  };

  // No track loaded → "Continue where you left off" shows the last TRACK you
  // worked on (resume it). A track IS loaded → "Your sections", filtered to that
  // track. Stem sections only ever appear in the context of a loaded track.
  const fileLoaded = !!currentRef;
  const fileSections = fileLoaded ? sections.filter((s) => s.video_id === currentRef) : [];
  const recent = sections[0];

  return (
    <aside className="st-rail">
      {!fileLoaded ? (
        recent ? (
          <div className="st-rail-card">
            <div className="st-rail-head">
              <div className="st-section-label">Continue where you left off</div>
            </div>
            <div className="st-sec-list">{renderTrack(recent)}</div>
          </div>
        ) : (
          <div className="st-rail-card">
            <div className="st-rail-head">
              <div className="st-section-label">Your sections</div>
            </div>
            <div className="st-sec-empty">
              No saved sections yet.
              <br />
              Isolate a part to save it here.
            </div>
          </div>
        )
      ) : (
        <div className="st-rail-card">
          <div className="st-rail-head">
            <div className="st-section-label">Your sections</div>
            <span className="st-rail-count">{fileSections.length}</span>
          </div>
          {fileSections.length === 0 ? (
            <div className="st-sec-empty">
              No sections for this track yet.
              <br />
              Isolate a part to save it here.
            </div>
          ) : (
            <div className="st-sec-list">{fileSections.map(renderSec)}</div>
          )}
        </div>
      )}

      <div className="st-tip">
        <div className="st-tip-head">
          <span className="st-tip-ic" aria-hidden="true">i</span>
          <b>Tip · tighter selections separate cleaner</b>
        </div>
        <p>Drag the handles on the waveform to grab just the phrase you need. Shorter regions also separate faster.</p>
      </div>
    </aside>
  );
}

/* ───────────────────────── shared bits ───────────────────────── */

function ErrorBanner({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div className="st-error">
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} aria-label="Dismiss">
        ×
      </button>
    </div>
  );
}

function SaveChoiceModal({
  name,
  onOverride,
  onNew,
  onCancel,
}: {
  name: string;
  onOverride: () => void;
  onNew: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="rl-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="save-choice-title">
      <div className="rl-modal">
        <h3 id="save-choice-title">Save section</h3>
        <p>
          You already saved “{name}”. Update it with the current mix, loop and speed — or keep it and save this as a new
          section?
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button onClick={onCancel} className="rl-btn-secondary">
            Cancel
          </button>
          <button onClick={onNew} className="rl-btn-secondary">
            Save as new
          </button>
          <button onClick={onOverride} className="rl-btn-primary">
            Update “{name}”
          </button>
        </div>
      </div>
    </div>
  );
}

function QuotaModal({ kind, onClose }: { kind: 'anon' | 'free'; onClose: () => void }) {
  const isAnon = kind === 'anon';
  return (
    <div className="rl-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="quota-title">
      <div className="rl-modal">
        <h3 id="quota-title">{isAnon ? 'Daily anonymous limit reached' : 'Daily free limit reached'}</h3>
        <p>
          {isAnon ? (
            <>
              You've used your separations for today as an anonymous visitor. Sign in with Google to bump that up for
              free, or upgrade to Pro for unlimited separations.
            </>
          ) : (
            <>
              You've used your free separations for today. Upgrade to Pro for unlimited separations, higher quality on
              demand, and longer clip lengths.
            </>
          )}
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button onClick={onClose} className="rl-btn-secondary">
            Maybe later
          </button>
          {isAnon && (
            <Link to="/login" className="rl-btn-secondary" style={{ textDecoration: 'none' }}>
              Sign in with Google
            </Link>
          )}
          <Link to="/pricing" className="rl-btn-primary" style={{ textDecoration: 'none' }}>
            Upgrade to Pro
          </Link>
        </div>
      </div>
    </div>
  );
}

function LoopCapModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="rl-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="loopcap-title">
      <div className="rl-modal">
        <h3 id="loopcap-title">3 loops per song on Free</h3>
        <p>
          You've saved 3 loops for this song. Upgrade to Pro for unlimited loops per song, plus longer clips and
          cross-device sync.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button onClick={onClose} className="rl-btn-secondary">
            Maybe later
          </button>
          <Link to="/pricing" className="rl-btn-primary" style={{ textDecoration: 'none' }}>
            Upgrade to Pro
          </Link>
        </div>
      </div>
    </div>
  );
}

function BestQualityModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="rl-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="bestq-title">
      <div className="rl-modal">
        <h3 id="bestq-title">Best quality is a Pro feature</h3>
        <p>
          <b>Best</b> is our cleanest separation — noticeably sharper stems, especially piano and vocals.
          Upgrade to Pro to unlock it, along with unlimited separations and longer clips.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button onClick={onClose} className="rl-btn-secondary">
            Stay on Standard
          </button>
          <Link to="/pricing" className="rl-btn-primary" style={{ textDecoration: 'none' }}>
            Upgrade to Pro
          </Link>
        </div>
      </div>
    </div>
  );
}

function ExportProModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="rl-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="exportpro-title">
      <div className="rl-modal">
        <h3 id="exportpro-title">Exporting is a Pro feature</h3>
        <p>
          Upgrade to Pro to download your mix and individual stems as audio files — plus unlimited
          separations, longer clips and cross-device sync. Export is available for tracks you upload yourself.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button onClick={onClose} className="rl-btn-secondary">
            Maybe later
          </button>
          <Link to="/pricing" className="rl-btn-primary" style={{ textDecoration: 'none' }}>
            Upgrade to Pro
          </Link>
        </div>
      </div>
    </div>
  );
}
